(() => {
  const FREE_SHIPPING_THRESHOLD = 15;

  const toInt = (value, fallback = 0) => {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const moneyFormatter = (currency) => {
    try {
      return new Intl.NumberFormat('es-ES', { style: 'currency', currency });
    } catch (error) {
      return new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  };

  const shippingText = (pairs, compact = false) => {
    const safePairs = Math.max(0, toInt(pairs, 0));
    if (safePairs >= FREE_SHIPPING_THRESHOLD) {
      return compact ? 'Portes gratis' : `✓ ${safePairs} pares · PORTES GRATIS`;
    }
    const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - safePairs);
    return compact
      ? `Faltan ${remaining} para portes gratis`
      : `${safePairs} pares · faltan ${remaining} para portes gratis`;
  };

  const updateShippingUI = (pairs) => {
    const safePairs = Math.max(0, toInt(pairs, 0));

    document.querySelectorAll('[data-clayan-b2b-cart-count]').forEach((badge) => {
      badge.textContent = String(safePairs);
    });

    document.querySelectorAll('[data-clayan-b2b-shipping-status]').forEach((el) => {
      const compact = el.closest('.clayan-b2b-summary-fab') !== null;
      el.dataset.currentPairs = String(safePairs);
      el.textContent = shippingText(safePairs, compact);
      el.classList.toggle('is-free', safePairs >= FREE_SHIPPING_THRESHOLD);
    });

    document.querySelectorAll('clayan-b2b-order').forEach((order) => {
      order.dataset.cartPairs = String(safePairs);
      if (typeof order.updateSummary === 'function') order.updateSummary();
    });
  };

  const refreshCartMeta = async () => {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) return;
      const cart = await response.json();
      updateShippingUI(cart.item_count || 0);
    } catch (error) {
      // Non-critical UI enhancement. Shopify cart remains functional if this request fails.
    }
  };

  class ClayanB2BOrder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.inputs = Array.from(this.querySelectorAll('[data-b2b-qty]'));
      this.totalPairs = this.querySelector('[data-b2b-total-pairs]');
      this.totalAmount = this.querySelector('[data-b2b-total-amount]');
      this.shippingPreview = this.querySelector('[data-b2b-shipping-preview]');
      this.status = this.querySelector('[data-b2b-status]');
      this.addButton = this.querySelector('[data-b2b-add]');
      this.addBlockReason = this.querySelector('[data-b2b-add-block-reason]');
      this.currency = this.dataset.currency || 'EUR';
      this.formatMoney = moneyFormatter(this.currency);
      this.isCampaign = this.dataset.b2bCampaign === 'true';
      this.campaignName = (this.dataset.b2bCampaignName || '').trim();
      this.campaignColor = this.querySelector('[data-b2b-campaign-color]');
      this.campaignFloor = this.querySelector('[data-b2b-campaign-floor]');
      this.campaignColorNote = this.querySelector('[data-b2b-campaign-color-note]');
      this.currentCampaignColor = (this.dataset.b2bCurrentColor || '').trim();
      this.currentCampaignFloor = (this.dataset.b2bCurrentFloor || '').trim();
      this.currentCampaignModel = (this.dataset.b2bCurrentModel || '').trim();
      this.campaignSection = (this.dataset.b2bCampaignSection || '').trim();
      this.closedSiblingMode = this.dataset.b2bClosedSiblingMode === 'true';
      this.currentCampaignFamily = (this.dataset.b2bCurrentFamily || '').trim();
      this.productPositiveStock = this.dataset.b2bProductPositiveStock === 'true';
      this.cartHasCampaign = this.dataset.b2bCartHasCampaign === 'true';
      this.cartHasStandard = this.dataset.b2bCartHasStandard === 'true';
      this.campaignEndpoint = String(this.dataset.b2bCampaignEndpoint || '/apps/clayan-b2b').replace(/\/$/, '');
      this.companyLocationId = String(this.dataset.b2bCompanyLocationId || '').trim();
      this.remoteCampaignReferences = [];
      this.storefrontCampaignReferences = [];
      this.remoteCampaignRequest = 0;
      this.siblingNavigator = this.querySelector('[data-b2b-sibling-navigator]');
      this.campaignFloorLabel = this.querySelector('[data-b2b-campaign-floor-label]');
      this.campaignCombinations = [];
      try {
        const rawCombinations = JSON.parse(this.dataset.b2bCampaignCombinations || '[]');
        this.campaignCombinations = Array.isArray(rawCombinations) ? rawCombinations.map(String) : [];
      } catch (error) {
        this.campaignCombinations = [];
      }
      this.activeCampaignPriceReference = null;
      this.activeCampaignMatchKind = 'none';
      this.campaignReferenceNodes = Array.from(this.querySelectorAll('[data-b2b-campaign-reference]'));
      this.campaignReferencePanel = this.querySelector('[data-b2b-campaign-reference-panel]');
      this.campaignReferenceTitle = this.querySelector('[data-b2b-campaign-reference-title]');
      this.campaignReferenceMessage = this.querySelector('[data-b2b-campaign-reference-message]');
      this.campaignReferenceLink = this.querySelector('[data-b2b-campaign-reference-link]');
      this.campaignReferenceImage = this.querySelector('[data-b2b-campaign-reference-image]');
      this.campaignSamples = this.querySelector('[data-b2b-campaign-samples]');
      this.campaignSamplesList = this.querySelector('[data-b2b-campaign-samples-list]');
      this.campaignOtherFinishes = this.querySelector('[data-b2b-campaign-other-finishes]');
      this.campaignOtherFinishesList = this.querySelector('[data-b2b-campaign-other-finishes-list]');
      this.campaignMainImageButton = this.querySelector('[data-b2b-campaign-main-image-button]');
      this.campaignMainImage = this.querySelector('[data-b2b-campaign-main-image]');
      this.campaignThumbs = this.querySelector('[data-b2b-campaign-thumbs]');
      this.campaignGalleryLabel = this.querySelector('[data-b2b-campaign-gallery-label]');
      this.campaignGalleryNote = this.querySelector('[data-b2b-campaign-gallery-note]');
      this.campaignLightbox = this.querySelector('[data-b2b-campaign-lightbox]');
      this.campaignLightboxImage = this.querySelector('[data-b2b-campaign-lightbox-image]');
      this.campaignLightboxCaption = this.querySelector('[data-b2b-campaign-lightbox-caption]');
      this.campaignLightboxPrev = this.querySelector('[data-b2b-campaign-lightbox-prev]');
      this.campaignLightboxNext = this.querySelector('[data-b2b-campaign-lightbox-next]');
      this.campaignLightboxCounter = this.querySelector('[data-b2b-campaign-lightbox-counter]');
      this.campaignLightboxItems = [];
      this.campaignLightboxIndex = 0;
      this.activeCampaignReference = null;
      this.mainCampaignGallery = this.closest('.clayan-b2b-product-view')?.querySelector('.clayan-b2b-product-view__gallery') || null;
      this.originalMainCampaignGalleryHTML = this.mainCampaignGallery?.innerHTML || '';
      this.originalCampaignImages = this.campaignThumbs
        ? Array.from(this.campaignThumbs.querySelectorAll('[data-b2b-campaign-thumb]')).map((button) => ({
            src: String(button.dataset.imageUrl || '').trim(),
            alt: String(button.dataset.imageAlt || '').trim(),
          })).filter((item) => item.src)
        : [];

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
      this.addButton?.addEventListener('click', this.addToCart.bind(this));
      this._campaignLightboxKeydown = (event) => {
        if (!this.campaignLightbox || this.campaignLightbox.hidden) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeCampaignLightbox();
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.stepCampaignLightbox(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.stepCampaignLightbox(1);
        }
      };
      document.addEventListener('keydown', this._campaignLightboxKeydown);
      if (this.isCampaign) {
        this.siblingNavigator?.addEventListener('change', (event) => {
          const url = String(event.target?.value || '').trim();
          if (url) window.location.assign(url);
        });
        this.campaignColor?.addEventListener('change', async () => {
          this.clearStatus();
          const fixed = this.syncCampaignFixedFloor();
          if (!fixed) this.syncCampaignFloorToRealReference();

          // Primero intentamos con referencias ya cargadas. Si todavía no están,
          // buscamos productos reales del mismo modelo en Storefront y después en
          // el servidor. Esto hace que ORION y el resto redirijan de forma fiable.
          if (this.navigateToExactCampaignProduct()) return;
          await this.refreshClosedSiblingReferencesFromStorefront().catch(() => {});
          if (!fixed) this.syncCampaignFloorToRealReference();
          if (this.navigateToExactCampaignProduct()) return;

          this.updateCampaignPricing();
          this.updateCampaignVisuals();
          await this.refreshCampaignReferenceFromServer().catch(() => {});
          if (this.navigateToExactCampaignProduct()) return;
          this.updateCampaignPricing();
          this.updateCampaignVisuals();
        });
        this.campaignColor?.addEventListener('input', () => {
          this.clearStatus();
          this.updateSummary();
        });
        this.campaignFloor?.addEventListener('change', async () => {
          this.clearStatus();
          this.syncCampaignFixedFloor();
          if (this.navigateToExactCampaignProduct()) return;
          await this.refreshClosedSiblingReferencesFromStorefront().catch(() => {});
          if (this.navigateToExactCampaignProduct()) return;
          this.updateCampaignPricing();
          this.updateCampaignVisuals();
          await this.refreshCampaignReferenceFromServer().catch(() => {});
          if (this.navigateToExactCampaignProduct()) return;
          this.updateCampaignPricing();
          this.updateCampaignVisuals();
        });
        this.campaignFloor?.addEventListener('input', () => {
          this.clearStatus();
          this.updateSummary();
        });
        this.hideCampaignRecommendations();
        this.ensureCampaignDefaults();
        this.renderCampaignSamples();
        this.syncCampaignFixedFloor();
        this.ensureCampaignDefaults();
        this.updateCampaignPricing();
        this.updateCampaignVisuals();
        this.refreshClosedSiblingReferencesFromStorefront().catch(() => {});
        this.refreshCampaignReferenceFromServer().catch(() => {});
      }
      this.updateSummary();
    }

    disconnectedCallback() {
      if (this._campaignLightboxKeydown) {
        document.removeEventListener('keydown', this._campaignLightboxKeydown);
      }
    }

    ensureCampaignDefaults() {
      if (!this.isCampaign) return;
      const pickFirst = (select) => {
        if (!select || select.tagName !== 'SELECT') return false;
        if (String(select.value || '').trim()) return false;
        const option = Array.from(select.options || []).find((item) => String(item.value || '').trim());
        if (!option) return false;
        select.value = option.value;
        return true;
      };
      pickFirst(this.campaignColor);
      this.syncCampaignFixedFloor();
      pickFirst(this.campaignFloor);
    }

    normalizeCampaignText(value = '') {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/nobuck\s+soft/g, 'nobuck')
        .replace(/\bnegra\b/g, 'negro')
        .replace(/\s+/g, ' ')
        .trim();
    }

    normalizeCampaignColor(value = '') {
      // El mismo acabado puede llegar desde Shopify/servidor con o sin el
      // sufijo "PISO ...". Para Colores disponibles siempre es UN color.
      const normalized = this.normalizeCampaignText(value);
      if (normalized.includes(' piso ')) {
        return normalized.split(' piso ', 1)[0].trim();
      }
      return normalized;
    }

    normalizeCampaignSection(value = '') {
      return this.normalizeCampaignText(value).replace(/[^a-z0-9]+/g, ' ').trim();
    }

    campaignMaterialFamily(value = '') {
      const normalized = this.normalizeCampaignText(value);
      return normalized.split(' ').filter(Boolean)[0] || '';
    }

    campaignProperties() {
      if (!this.isCampaign) return {};
      const color = String(this.campaignColor?.value || '').trim();
      const floor = String(this.campaignFloor?.value || '').trim();

      if (this.campaignColor && !color) {
        this.setStatus('Selecciona la piel / color antes de añadir el pedido.', true);
        this.campaignColor.focus?.();
        return null;
      }
      if (this.campaignFloor && !floor) {
        this.setStatus('Selecciona el piso antes de añadir el pedido.', true);
        this.campaignFloor.focus?.();
        return null;
      }

      const properties = {};
      if (this.campaignName) properties['Campaña'] = this.campaignName;
      if (color) properties['Piel / Color'] = color;
      if (floor) properties['Piso'] = floor;
      properties['_Campaña modelo'] = this.currentCampaignModel || '';
      properties['_Campaña sección'] = this.campaignSection || '';
      properties['_Campaña virtual'] = this.isCurrentCampaignSelectionVirtual() ? '1' : '0';
      return properties;
    }

    localCampaignReferenceData() {
      return this.campaignReferenceNodes.map((node) => {
        const images = Array.from(node.querySelectorAll('[data-b2b-reference-media]')).map((media) => ({
          src: String(media.dataset.imageUrl || '').trim(),
          alt: String(media.dataset.imageAlt || '').trim(),
        })).filter((item) => item.src);
        const fallbackImage = String(node.dataset.referenceImage || '').trim();
        if (!images.length && fallbackImage) {
          images.push({ src: fallbackImage, alt: String(node.dataset.referenceTitle || '').trim() });
        }
        const variants = {};
        Array.from(node.querySelectorAll('[data-b2b-reference-variant]')).forEach((variantNode) => {
          const size = this.normalizeCampaignText(variantNode.dataset.size || '');
          if (!size) return;
          variants[size] = {
            id: String(variantNode.dataset.variantId || ''),
            price: Math.max(0, toInt(variantNode.dataset.price, 0)),
          };
        });
        return {
          productId: String(node.dataset.referenceProductId || ''),
          model: String(node.dataset.referenceModel || '').trim(),
          section: String(node.dataset.referenceSection || '').trim(),
          color: String(node.dataset.referenceColor || '').trim(),
          floor: String(node.dataset.referenceFloor || '').trim(),
          title: String(node.dataset.referenceTitle || '').trim(),
          url: String(node.dataset.referenceUrl || '').trim(),
          image: fallbackImage,
          images,
          variants,
          remote: false,
          priceOnly: false,
        };
      });
    }

    campaignReferenceData() {
      // Las referencias del servidor van primero: no dependen del límite de
      // productos que Liquid haya podido renderizar en la colección.
      const all = [
        ...(this.remoteCampaignReferences || []),
        ...(this.storefrontCampaignReferences || []),
        ...this.localCampaignReferenceData(),
      ];
      const seen = new Set();
      return all.filter((item) => {
        const key = item.priceOnly
          ? ['price', this.normalizeCampaignSection(item.section || this.campaignSection), this.normalizeCampaignText(item.priceForColor || item.color), this.normalizeCampaignText(item.floor)].join('|')
          : ['visual', this.normalizeCampaignSection(item.section || this.campaignSection), this.normalizeCampaignText(item.model), this.normalizeCampaignText(item.color), this.normalizeCampaignText(item.floor)].join('|');
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }

    normalizeRemoteCampaignReference(raw, { priceOnly = false, priceForColor = '' } = {}) {
      if (!raw || typeof raw !== 'object') return null;
      const variants = {};
      Object.entries(raw.variants || {}).forEach(([size, value]) => {
        const key = this.normalizeCampaignText(size);
        if (!key || !value) return;
        variants[key] = {
          id: String(value.id || ''),
          price: Math.max(0, toInt(value.price, 0)),
        };
      });
      const images = Array.isArray(raw.images)
        ? raw.images.map((item) => ({ src: String(item?.src || ''), alt: String(item?.alt || '') })).filter((item) => item.src)
        : [];
      const image = String(raw.image || images[0]?.src || '').trim();
      if (!images.length && image) images.push({ src: image, alt: String(raw.title || '') });
      return {
        productId: String(raw.productId || ''),
        model: String(raw.model || '').trim(),
        section: String(raw.section || this.campaignSection || '').trim(),
        color: String(raw.color || '').trim(),
        sourceColor: String(raw.sourceColor || raw.color || '').trim(),
        floor: String(raw.floor || '').trim(),
        title: String(raw.title || '').trim(),
        url: String(raw.url || '').trim(),
        image,
        images,
        variants,
        remote: true,
        priceOnly: Boolean(priceOnly),
        priceForColor: String(priceForColor || raw.priceForColor || '').trim(),
        defaultPrice: Math.max(0, toInt(raw.defaultPrice, 0)),
      };
    }

    closedColorFromTitle(title = '') {
      let value = String(title || '').trim();
      const model = String(this.currentCampaignModel || '').trim();
      if (model) {
        const escaped = model.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        value = value.replace(new RegExp(`^\\s*${escaped}\\s*(?:[·\\-:]\\s*)?`, 'i'), '');
      }
      value = value.split(/\\bPISO\\b/i)[0];
      if (this.normalizeCampaignSection(this.campaignSection) === 'sandalias') {
        value = value.replace(/\\b7743\\b/gi, '');
      }
      return value.replace(/^[·:\s-]+|[·:\s-]+$/g, '').replace(/\\s+/g, ' ').trim();
    }

    campaignFloorFromTitle(title = '') {
      const raw = String(title || '').trim();
      const match = raw.match(/\bPISO\b\s+(.+)$/i);
      if (!match) return '';
      return String(match[1] || '').replace(/^[·:\s-]+|[·:\s-]+$/g, '').replace(/\s+/g, ' ').trim();
    }

    campaignModelFromTitle(title = '') {
      const raw = String(title || '').trim();
      const match = raw.match(/^\s*(\d{1,5}[A-Za-z]?)\b/i);
      return match ? String(match[1] || '').trim() : '';
    }

    campaignColorFromAnyTitle(title = '') {
      let value = String(title || '').trim();
      value = value.replace(/^\s*\d{1,5}[A-Za-z]?\s*(?:[·\-:]\s*)?/i, '');
      value = value.split(/\bPISO\b/i)[0];
      return value
        .replace(/^[·:\s-]+|[·:\s-]+$/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    async storefrontCampaignSearch(queryText) {
      const query = String(queryText || '').trim();
      if (!query) return [];
      const root = window.Shopify?.routes?.root || '/';
      const params = new URLSearchParams();
      params.set('q', query);
      params.set('resources[type]', 'product');
      params.set('resources[limit]', '10');
      params.set('resources[options][unavailable_products]', 'show');

      const response = await fetch(`${root}search/suggest.json?${params.toString()}`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });
      if (!response.ok) return [];
      const data = await response.json();
      const products = data?.resources?.results?.products || [];
      return Array.isArray(products) ? products : [];
    }

    async refreshClosedSiblingReferencesFromStorefront() {
      if (!this.currentCampaignModel) return;

      const wantedModel = this.normalizeCampaignText(this.currentCampaignModel);
      const selectedColorRaw = String(this.campaignColor?.value || this.currentCampaignColor || '').trim();
      const selectedColor = this.normalizeCampaignColor(selectedColorRaw);
      const selectedFloorRaw = String(this.campaignFloor?.value || this.currentCampaignFloor || '').trim();
      const section = this.normalizeCampaignSection(this.campaignSection);
      const fallbackFloor = selectedFloorRaw || String(this.currentCampaignFloor || '').trim();

      try {
        const queries = [String(this.currentCampaignModel || '').trim()];

        // ORION normal: además del mismo modelo, buscamos el acabado en TODA la
        // sección. Así una combinación no fotografiada en 362 puede usar como
        // referencia, por ejemplo, la muestra exacta del 361.
        if (section === 'orion' && selectedColorRaw) {
          queries.push(`${selectedColorRaw} ORION`);
        }

        const batches = await Promise.all(
          Array.from(new Set(queries.filter(Boolean))).map((query) =>
            this.storefrontCampaignSearch(query).catch(() => [])
          )
        );

        const refs = [];
        const seen = new Set();

        batches.flat().forEach((product) => {
          const title = String(product?.title || '').trim();
          if (!title) return;

          const productModel = this.campaignModelFromTitle(title);
          const modelNorm = this.normalizeCampaignText(productModel);
          const color = this.campaignColorFromAnyTitle(title);
          const colorNorm = this.normalizeCampaignColor(color);
          const floor = this.campaignFloorFromTitle(title) || fallbackFloor;
          const floorNorm = this.normalizeCampaignText(floor);

          const isSameModel = modelNorm === wantedModel;

          let validCrossReference = false;
          if (section === 'orion' && selectedColor) {
            const looksOrion = /\bpiso\s+orion\b/i.test(title) || floorNorm.startsWith('orion ');
            validCrossReference = looksOrion && colorNorm === selectedColor;
          }

          if (!isSameModel && !validCrossReference) return;
          if (!color) return;

          const rawImage = product?.image || product?.featured_image || null;
          const image = typeof rawImage === 'string'
            ? rawImage
            : String(rawImage?.url || rawImage?.src || rawImage?.original_src || '').trim();

          const root = window.Shopify?.routes?.root || '/';
          const url = String(
            product?.url
            || (product?.handle ? `${root}products/${product.handle}` : '')
            || ''
          ).trim();

          const key = [modelNorm, colorNorm, floorNorm, url.replace(/\/$/, '')].join('|');
          if (seen.has(key)) return;
          seen.add(key);

          refs.push({
            productId: String(product?.id || ''),
            model: productModel || String(this.currentCampaignModel || '').trim(),
            section: String(this.campaignSection || '').trim(),
            color,
            sourceColor: color,
            floor,
            title,
            url,
            image,
            images: image ? [{ src: image, alt: title }] : [],
            variants: {},
            remote: false,
            storefront: true,
            priceOnly: false,
          });
        });

        if (!refs.length) return;

        this.storefrontCampaignReferences = refs;

        // Si el acabado solo está fotografiado con otra suela, usamos la suela
        // de esa muestra real como selección automática.
        this.syncCampaignFloorToRealReference();

        this.syncClosedSiblingNavigator();
        this.renderCampaignSamples();
        this.updateCampaignSampleHighlight();
        this.updateCampaignPricing();
        this.updateCampaignVisuals();
      } catch (error) {
        // Si Predictive Search no está disponible, quedan los fallbacks Liquid
        // y servidor B2B.
      }
    }

    async refreshCampaignReferenceFromServer() {
      if (!this.isCampaign || !this.companyLocationId || !this.campaignSection) return;
      let selectedColor = String(this.campaignColor?.value || this.currentCampaignColor || '').trim();
      const selectedFloor = String(this.campaignFloor?.value || this.currentCampaignFloor || '').trim();
      if (this.closedSiblingMode) {
        selectedColor = selectedColor.split(/\bPISO\b/i)[0].trim();
        if (this.normalizeCampaignSection(this.campaignSection) === 'sandalias') {
          selectedColor = selectedColor.replace(/\b7743\b/gi, '').replace(/[·\-]+$/g, '').trim();
        }
      }
      if (!selectedColor) return;

      const requestId = ++this.remoteCampaignRequest;
      const params = new URLSearchParams({
        company_location_id: this.companyLocationId,
        section: this.campaignSection,
        model: this.currentCampaignModel || '',
        color: selectedColor,
        floor: selectedFloor,
      });

      try {
        const response = await fetch(`${this.campaignEndpoint}/campaign-reference?${params.toString()}`, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'ngrok-skip-browser-warning': '1',
          },
        });
        if (!response.ok) return;
        const data = await response.json();
        if (requestId !== this.remoteCampaignRequest) return;

        const refs = [];
        const visual = this.normalizeRemoteCampaignReference(data.visual_reference);
        if (visual) refs.push(visual);

        const price = this.normalizeRemoteCampaignReference(data.price_reference, {
          priceOnly: true,
          priceForColor: selectedColor,
        });
        if (price) refs.push(price);

        (Array.isArray(data.model_samples) ? data.model_samples : []).forEach((sampleRaw) => {
          const sample = this.normalizeRemoteCampaignReference(sampleRaw);
          if (sample) refs.push(sample);
        });

        this.remoteCampaignReferences = refs;
        this.syncClosedSiblingNavigator();
        this.renderCampaignSamples();
        this.updateCampaignPricing();
        this.updateCampaignVisuals();
      } catch (error) {
        // Fallback silencioso: las referencias Liquid siguen funcionando si el
        // endpoint temporalmente no está disponible.
      }
    }

    hideCampaignRecommendations() {
      if (!this.isCampaign) return;
      document.body?.classList.add('clayan-b2b-campaign-product');
      const selectors = [
        'related-products',
        'product-recommendations',
        '.related-products',
        '[id*="related-products"]',
        '[id*="product-recommendations"]',
      ];
      document.querySelectorAll(selectors.join(',')).forEach((node) => {
        const section = node.closest('.shopify-section') || node;
        section.hidden = true;
        section.setAttribute('aria-hidden', 'true');
      });
    }


    syncClosedSiblingNavigator() {
      if (!this.closedSiblingMode || !this.siblingNavigator || !this.currentCampaignModel) return;
      const currentModel = this.normalizeCampaignText(this.currentCampaignModel);
      const currentColor = this.normalizeCampaignColor(this.currentCampaignColor || this.campaignColor?.value || '');
      const section = this.normalizeCampaignSection(this.campaignSection);
      const family = this.campaignMaterialFamily(this.currentCampaignColor || this.campaignColor?.value || '');

      const grouped = new Map();
      this.campaignReferenceData().forEach((item) => {
        if (item.priceOnly || this.normalizeCampaignText(item.model) !== currentModel) return;
        if (!item.color) return;
        if (section === 'sandalias' && family && this.campaignMaterialFamily(item.color) !== family) return;
        const key = this.normalizeCampaignColor(item.color);
        if (!key) return;
        const existing = grouped.get(key);
        const score = (item.image ? 4 : 0) + (item.url ? 2 : 0) + (item.remote ? 1 : 0);
        const oldScore = existing ? ((existing.image ? 4 : 0) + (existing.url ? 2 : 0) + (existing.remote ? 1 : 0)) : -1;
        if (!existing || score > oldScore) grouped.set(key, item);
      });

      const samples = Array.from(grouped.values());
      if (!samples.length) return;

      const currentUrl = this.siblingNavigator.value || window.location.pathname;
      this.siblingNavigator.innerHTML = '';
      samples.forEach((sample) => {
        const option = document.createElement('option');
        option.value = sample.url || currentUrl;
        option.textContent = sample.color || sample.title || 'Muestra';
        const selected = this.normalizeCampaignColor(sample.color) === currentColor;
        if (selected) option.selected = true;
        this.siblingNavigator.appendChild(option);
      });
    }

    setCampaignCompactGallery(images, label, note, reference = false) {
      const safeImages = Array.isArray(images) ? images.filter((item) => item?.src) : [];
      if (!safeImages.length) return;
      if (this.campaignGalleryLabel && label) this.campaignGalleryLabel.textContent = label;
      if (this.campaignGalleryNote && note) this.campaignGalleryNote.textContent = note;
      const visuals = this.querySelector('.clayan-b2b-campaign-visuals__sticky');
      visuals?.classList.toggle('is-reference', Boolean(reference));

      if (this.campaignThumbs) {
        this.campaignThumbs.innerHTML = '';
        safeImages.slice(0, 6).forEach((item, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = `clayan-b2b-campaign-visuals__thumb${index === 0 ? ' is-active' : ''}`;
          button.dataset.b2bCampaignThumb = '';
          button.dataset.imageUrl = item.src;
          button.dataset.imageAlt = item.alt || label || 'Foto de campaña';
          button.setAttribute('aria-label', `Ver foto ${index + 1}`);
          const img = document.createElement('img');
          img.src = item.src;
          img.alt = item.alt || label || 'Foto de campaña';
          img.loading = 'lazy';
          button.appendChild(img);
          this.campaignThumbs.appendChild(button);
        });
      }
      this.setCampaignMainImage(safeImages[0]);
    }

    setCampaignMainImage(image) {
      if (!image?.src || !this.campaignMainImage) return;
      this.campaignMainImage.src = image.src;
      this.campaignMainImage.alt = image.alt || 'Foto de campaña';
      this.campaignThumbs?.querySelectorAll('[data-b2b-campaign-thumb]').forEach((thumb) => {
        thumb.classList.toggle('is-active', String(thumb.dataset.imageUrl || '') === image.src);
      });
    }

    restoreMainCampaignGallery() {
      if (this.mainCampaignGallery && this.originalMainCampaignGalleryHTML) {
        this.mainCampaignGallery.innerHTML = this.originalMainCampaignGalleryHTML;
      }
    }

    renderExactReferenceInMainGallery(images, label) {
      if (!this.mainCampaignGallery || !Array.isArray(images) || !images.length) return;
      this.mainCampaignGallery.innerHTML = '';
      images.slice(0, 6).forEach((item, index) => {
        const figure = document.createElement('figure');
        figure.className = `clayan-b2b-product-view__media${index === 0 ? ' clayan-b2b-product-view__media--main' : ''}`;
        figure.dataset.b2bCampaignMedia = '';
        figure.dataset.b2bMediaAlt = item.alt || label || 'Foto de campaña';
        figure.dataset.b2bModelGalleryItem = '';
        figure.dataset.imageUrl = item.src;
        figure.setAttribute('tabindex', '0');
        figure.setAttribute('role', 'button');
        figure.setAttribute('aria-label', `Ampliar foto ${index + 1} del modelo ${this.currentCampaignModel || ''}`);
        const img = document.createElement('img');
        img.src = item.src;
        img.alt = item.alt || label || 'Foto de campaña';
        img.loading = index === 0 ? 'eager' : 'lazy';
        img.className = 'clayan-b2b-product-view__image';
        figure.appendChild(img);
        this.mainCampaignGallery.appendChild(figure);
      });
    }

    campaignReferenceMatch(selectedColor, selectedFloor) {
      const section = this.normalizeCampaignSection(this.campaignSection);
      const refs = this.campaignReferenceData().filter((item) => {
        if (item.priceOnly) return false;
        const itemSection = this.normalizeCampaignSection(item.section || this.campaignSection);
        return !section || !itemSection || itemSection === section;
      });
      const color = this.normalizeCampaignColor(selectedColor);
      const floor = this.normalizeCampaignText(selectedFloor);
      const model = this.normalizeCampaignText(this.currentCampaignModel);
      if (!color) return { reference: null, kind: 'none' };

      let colorRefs = refs.filter((item) => this.normalizeCampaignColor(item.color) === color);
      const family = this.campaignMaterialFamily(color);
      const vitoNobuck = section === 'vito' && family === 'nobuck';
      if (vitoNobuck && this.normalizeCampaignText(this.currentCampaignModel) !== '7202') {
        colorRefs = colorRefs.filter((item) => this.normalizeCampaignText(item.model) !== '7202');
      }

      const sameModel = colorRefs.filter((item) => this.normalizeCampaignText(item.model) === model);
      const floorMatches = (item) => !floor || !item.floor || this.normalizeCampaignText(item.floor) === floor;
      const exact = sameModel.find((item) => floorMatches(item));
      if (exact) return { reference: exact, kind: 'exact' };
      if (sameModel.length) return { reference: sameModel[0], kind: 'same-model-other-floor' };
      const sameFloor = colorRefs.find((item) => floorMatches(item));
      if (sameFloor) return { reference: sameFloor, kind: 'other-model-same-floor' };
      if (colorRefs.length) return { reference: colorRefs[0], kind: 'other-model' };
      return { reference: null, kind: 'none' };
    }

    exactCampaignProductUrl(selectedColor = '', selectedFloor = '') {
      if (!this.isCampaign || !this.currentCampaignModel) return '';
      const color = String(selectedColor || this.campaignColor?.value || '').trim();
      const floor = String(selectedFloor || this.campaignFloor?.value || '').trim();
      if (!color) return '';

      const match = this.campaignReferenceMatch(color, floor);
      const reference = match?.reference || null;
      if (!reference || match.kind !== 'exact' || !reference.url) return '';
      if (this.normalizeCampaignText(reference.model) !== this.normalizeCampaignText(this.currentCampaignModel)) return '';

      try {
        const target = new URL(reference.url, window.location.origin);
        const targetPath = target.pathname.replace(/\/$/, '');
        const currentPath = window.location.pathname.replace(/\/$/, '');
        if (!targetPath || targetPath === currentPath) return '';
        return target.href;
      } catch (error) {
        return '';
      }
    }

    navigateToExactCampaignProduct(selectedColor = '', selectedFloor = '') {
      const url = this.exactCampaignProductUrl(selectedColor, selectedFloor);
      if (!url) return false;
      window.location.assign(url);
      return true;
    }

    campaignPriceReferenceMatch(selectedColor, selectedFloor) {
      const selectedNormalized = this.normalizeCampaignColor(selectedColor);
      const serverPriceReference = this.campaignReferenceData().find((item) =>
        item.priceOnly
        && this.normalizeCampaignColor(item.priceForColor) === selectedNormalized
        && (!this.campaignSection || this.normalizeCampaignSection(item.section) === this.normalizeCampaignSection(this.campaignSection))
      );
      if (serverPriceReference) return { reference: serverPriceReference, kind: 'server-price-reference' };

      const visual = this.campaignReferenceMatch(selectedColor, selectedFloor);
      if (visual?.reference) return visual;

      const section = this.normalizeCampaignSection(this.campaignSection);
      const family = this.campaignMaterialFamily(selectedColor);
      const floor = this.normalizeCampaignText(selectedFloor);
      const model = this.normalizeCampaignText(this.currentCampaignModel);
      if (!family) return { reference: null, kind: 'none' };

      let familyRefs = this.campaignReferenceData().filter((item) => {
        if (item.priceOnly) return false;
        const itemSection = this.normalizeCampaignSection(item.section || this.campaignSection);
        const sameSection = !section || !itemSection || itemSection === section;
        return sameSection && this.campaignMaterialFamily(item.color) === family;
      });

      // En VITO no se usa el modelo 7202 como referencia de un Nobuck no fotografiado.
      if (section === 'vito' && family === 'nobuck' && model !== '7202') {
        familyRefs = familyRefs.filter((item) => this.normalizeCampaignText(item.model) !== '7202');
      }

      const floorMatches = (item) => !floor || !item.floor || this.normalizeCampaignText(item.floor) === floor;
      return { reference:
          familyRefs.find((item) => this.normalizeCampaignText(item.model) === model && floorMatches(item))
          || familyRefs.find((item) => this.normalizeCampaignText(item.model) === model)
          || familyRefs.find((item) => floorMatches(item))
          || familyRefs[0]
          || null,
        kind: 'material-price-reference'
      };
    }

    syncCampaignFloorToRealReference() {
      if (!this.isCampaign || !this.campaignColor || !this.campaignFloor) return false;

      const selectedColor = String(this.campaignColor.value || '').trim();
      const currentFloor = String(this.campaignFloor.value || '').trim();
      if (!selectedColor) return false;

      const match = this.campaignReferenceMatch(selectedColor, currentFloor);
      const referenceFloor = String(match?.reference?.floor || '').trim();
      if (!referenceFloor) return false;

      const normalizedReferenceFloor = this.normalizeCampaignText(referenceFloor);
      const currentNormalizedFloor = this.normalizeCampaignText(currentFloor);
      if (normalizedReferenceFloor === currentNormalizedFloor) return false;

      const option = Array.from(this.campaignFloor.options || []).find(
        (item) => this.normalizeCampaignText(item.value || item.textContent || '') === normalizedReferenceFloor
      );
      if (!option) return false;

      this.campaignFloor.value = option.value;
      this.campaignFloor.dataset.autoSelectedFromReference = 'true';
      return true;
    }

    openCampaignLightbox() {
      if (!this.campaignMainImage?.src) return;
      const currentSrc = this.campaignMainImage.currentSrc || this.campaignMainImage.src;
      const thumbs = this.campaignThumbs
        ? Array.from(this.campaignThumbs.querySelectorAll('[data-b2b-campaign-thumb]')).map((thumb) => ({
            src: String(thumb.dataset.imageUrl || '').trim(),
            alt: String(thumb.dataset.imageAlt || '').trim(),
          })).filter((item) => item.src)
        : [];
      const items = thumbs.length ? thumbs : [{
        src: currentSrc,
        alt: this.campaignMainImage.alt || 'Foto ampliada',
      }];
      const startIndex = Math.max(0, items.findIndex((item) => item.src === currentSrc));
      this.openCampaignLightboxGallery(
        items,
        this.campaignGalleryLabel?.textContent || '',
        startIndex
      );
    }

    openCampaignLightboxGallery(items, caption = '', startIndex = 0) {
      if (!this.campaignLightbox || !this.campaignLightboxImage) return;
      const safeItems = Array.isArray(items) ? items.filter((item) => item?.src) : [];
      if (!safeItems.length) return;

      this.campaignLightboxItems = safeItems;
      this.campaignLightboxIndex = Math.min(Math.max(0, Number(startIndex) || 0), safeItems.length - 1);
      this.campaignLightboxBaseCaption = caption || '';
      this.renderCampaignLightboxItem();
      this.campaignLightbox.hidden = false;
      document.documentElement.classList.add('clayan-b2b-lightbox-open');
    }

    renderCampaignLightboxItem() {
      if (!this.campaignLightboxItems?.length || !this.campaignLightboxImage) return;
      const item = this.campaignLightboxItems[this.campaignLightboxIndex];
      this.campaignLightboxImage.src = item.src;
      this.campaignLightboxImage.alt = item.alt || this.campaignLightboxBaseCaption || 'Foto ampliada';

      if (this.campaignLightboxCaption) {
        this.campaignLightboxCaption.textContent = item.alt || this.campaignLightboxBaseCaption || '';
      }
      if (this.campaignLightboxCounter) {
        this.campaignLightboxCounter.textContent = this.campaignLightboxItems.length > 1
          ? `${this.campaignLightboxIndex + 1} / ${this.campaignLightboxItems.length}`
          : '';
      }

      const multiple = this.campaignLightboxItems.length > 1;
      if (this.campaignLightboxPrev) this.campaignLightboxPrev.hidden = !multiple;
      if (this.campaignLightboxNext) this.campaignLightboxNext.hidden = !multiple;
    }

    stepCampaignLightbox(direction) {
      if (!this.campaignLightboxItems?.length || this.campaignLightboxItems.length < 2) return;
      const length = this.campaignLightboxItems.length;
      this.campaignLightboxIndex = (this.campaignLightboxIndex + direction + length) % length;
      this.renderCampaignLightboxItem();
    }

    closeCampaignLightbox() {
      if (!this.campaignLightbox) return;
      this.campaignLightbox.hidden = true;
      this.campaignLightboxItems = [];
      this.campaignLightboxIndex = 0;
      document.documentElement.classList.remove('clayan-b2b-lightbox-open');
    }

    renderCampaignSamples() {
      if (!this.campaignSamples || !this.campaignSamplesList || !this.currentCampaignModel) return;
      const currentModel = this.normalizeCampaignText(this.currentCampaignModel);
      const currentFamily = this.campaignMaterialFamily(this.currentCampaignColor || this.campaignColor?.value || '');
      const section = this.normalizeCampaignSection(this.campaignSection);
      const selectedFloor = this.normalizeCampaignText(this.campaignFloor?.value || this.currentCampaignFloor || '');

      // "Colores disponibles" es una vista por COLOR, no por origen técnico.
      // Si la misma muestra llega por Liquid + sibling + servidor, solo se pinta una vez.
      const grouped = new Map();
      this.campaignReferenceData().forEach((item) => {
        if (item.priceOnly) return;
        if (this.normalizeCampaignText(item.model) !== currentModel) return;
        const key = this.normalizeCampaignColor(item.color);
        if (!key) return;

        const floor = this.normalizeCampaignText(item.floor);
        const score =
          (item.image ? 8 : 0)
          + (item.url ? 4 : 0)
          + (selectedFloor && floor === selectedFloor ? 3 : 0)
          + (item.remote ? 1 : 0);
        const previous = grouped.get(key);
        const previousFloor = this.normalizeCampaignText(previous?.floor || '');
        const previousScore = previous
          ? ((previous.image ? 8 : 0) + (previous.url ? 4 : 0) + (selectedFloor && previousFloor === selectedFloor ? 3 : 0) + (previous.remote ? 1 : 0))
          : -1;
        if (!previous || score > previousScore) grouped.set(key, item);
      });
      let allSamples = Array.from(grouped.values()).filter((sample, index, list) => {
        const colorKey = this.normalizeCampaignColor(sample.color || '');
        const modelKey = this.normalizeCampaignText(sample.model || '');
        return list.findIndex((other) =>
          this.normalizeCampaignColor(other.color || '') === colorKey
          && this.normalizeCampaignText(other.model || '') === modelKey
        ) === index;
      });

      if (section === 'steven' && currentModel === '347') {
        const allowed347 = new Set(['serraje trebol', 'serraje arena']);
        allSamples = allSamples.filter((sample) =>
          allowed347.has(this.normalizeCampaignColor(sample.color || ''))
        );
      }

      const mainSamples = section === 'sandalias' && currentFamily
        ? allSamples.filter((item) => this.campaignMaterialFamily(item.color) === currentFamily)
        : allSamples;
      const otherSamples = section === 'sandalias' && currentFamily
        ? allSamples.filter((item) => this.campaignMaterialFamily(item.color) !== currentFamily)
        : [];

      if (!mainSamples.length && !otherSamples.length) {
        this.campaignSamples.hidden = true;
        return;
      }

      const buildCard = (sample, extraClass = '') => {
        const card = document.createElement('button');
        card.type = 'button';
        card.className = `clayan-b2b-color-card${extraClass ? ` ${extraClass}` : ''}`;
        card.dataset.sampleColor = sample.color || '';
        card.dataset.sampleFloor = sample.floor || '';
        card.dataset.sampleProductId = sample.productId || '';
        card.dataset.sampleModel = sample.model || '';
        card.dataset.sampleUrl = sample.url || '';
        card.dataset.b2bCampaignSampleSelect = '';
        card.setAttribute('aria-pressed', 'false');
        card.title = `Elegir ${sample.color || sample.title || 'muestra'}`;

        const imageWrap = document.createElement('span');
        imageWrap.className = 'clayan-b2b-color-card__image';
        const imageSrc = String(sample.image || sample.images?.[0]?.src || '').trim();
        if (imageSrc) {
          const img = document.createElement('img');
          img.src = imageSrc;
          img.alt = sample.title || sample.color || 'Muestra real';
          img.loading = 'lazy';
          img.addEventListener('error', () => {
            imageWrap.classList.add('is-image-missing');
            img.remove();
          }, { once: true });
          imageWrap.appendChild(img);
        } else {
          imageWrap.classList.add('is-image-missing');
        }
        card.appendChild(imageWrap);

        const label = document.createElement('span');
        label.className = 'clayan-b2b-color-card__label';
        label.textContent = sample.color || sample.title || 'Muestra';
        card.appendChild(label);

        const current = document.createElement('small');
        current.className = 'clayan-b2b-color-card__current';
        current.textContent = 'Actual';
        current.hidden = true;
        card.appendChild(current);
        return card;
      };

      this.campaignSamplesList.innerHTML = '';
      mainSamples.forEach((sample) => this.campaignSamplesList.appendChild(buildCard(sample)));

      if (this.campaignOtherFinishes && this.campaignOtherFinishesList) {
        this.campaignOtherFinishesList.innerHTML = '';
        otherSamples.forEach((sample) => this.campaignOtherFinishesList.appendChild(buildCard(sample, 'clayan-b2b-color-card--other')));
        this.campaignOtherFinishes.hidden = otherSamples.length === 0;
      }

      const headText = this.campaignSamples.querySelector('.clayan-b2b-colors-available__head p');
      if (headText) {
        headText.textContent = section === 'sandalias'
          ? `Elige otro color real de ${this.currentCampaignFamily || currentFamily || 'este acabado'}.`
          : 'Elige otro color real del mismo modelo. El color actual queda marcado.';
      }
      this.campaignSamples.hidden = false;
      this.updateCampaignSampleHighlight();
    }

    updateCampaignSampleHighlight() {
      if (!this.campaignSamplesList) return;
      const color = this.normalizeCampaignColor(this.campaignColor?.value || '');
      const floor = this.normalizeCampaignText(this.campaignFloor?.value || '');
      const lists = [this.campaignSamplesList, this.campaignOtherFinishesList].filter(Boolean);
      lists.forEach((list) => list.querySelectorAll('[data-sample-color]').forEach((card) => {
        const cardColor = this.normalizeCampaignColor(card.dataset.sampleColor || '');
        const cardFloor = this.normalizeCampaignText(card.dataset.sampleFloor || '');
        const selected = Boolean(color) && cardColor === color && (!floor || !cardFloor || cardFloor === floor);
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-pressed', selected ? 'true' : 'false');
        const current = card.querySelector('.clayan-b2b-color-card__current');
        if (current) current.hidden = !selected;
      }));
    }


    showCampaignReference(reference, selectedColor, selectedFloor) {
      if (!this.campaignReferencePanel || !reference) return false;
      this.activeCampaignReference = reference;
      const sameModel = this.normalizeCampaignText(reference.model) === this.normalizeCampaignText(this.currentCampaignModel);
      const color = selectedColor || reference.color || 'este acabado';
      const selectedFloorText = selectedFloor ? ` · ${selectedFloor}` : '';
      const realFloor = String(reference.floor || '').trim();
      const realFloorText = realFloor ? ` · ${realFloor}` : '';
      const sameFloor = !selectedFloor || !realFloor
        || this.normalizeCampaignText(selectedFloor) === this.normalizeCampaignText(realFloor);

      if (sameModel && sameFloor) {
        this.campaignReferencePanel.hidden = true;
        this.campaignReferencePanel.setAttribute('hidden', '');
        return false;
      }

      if (this.campaignReferenceTitle) {
        this.campaignReferenceTitle.textContent = sameModel
          ? 'REFERENCIA DEL ACABADO · MISMO MODELO'
          : 'REFERENCIA DE COLOR · NO ES EL MODELO QUE COMPRAS';
      }
      if (this.campaignReferenceMessage) {
        if (sameModel) {
          this.campaignReferenceMessage.textContent = `Estás comprando el modelo ${this.currentCampaignModel}${selectedFloorText}. Esta foto sirve solo para ver cómo queda ${color}${realFloorText} en el mismo modelo. El pedido respetará exactamente la selección que hagas.`;
        } else {
          this.campaignReferenceMessage.textContent = `ESTÁS COMPRANDO EL MODELO ${this.currentCampaignModel}. La foto de referencia corresponde al modelo ${reference.model || 'de referencia'} y se muestra únicamente para que veas el acabado ${color}${realFloorText}. El modelo de tu pedido NO cambia.`;
        }
      }
      if (this.campaignReferenceLink) {
        this.campaignReferenceLink.href = reference.url || '#';
        this.campaignReferenceLink.hidden = !reference.url;
      }
      if (this.campaignReferenceImage) {
        const imageSrc = String(reference.image || reference.images?.[0]?.src || '').trim();
        if (imageSrc) {
          this.campaignReferenceImage.src = imageSrc;
          this.campaignReferenceImage.alt = reference.title || `${color} ${reference.model}`;
          this.campaignReferenceImage.hidden = false;
          this.campaignReferenceImage.onerror = () => {
            this.campaignReferenceImage.hidden = true;
            this.campaignReferenceImage.removeAttribute('src');
          };
        } else {
          this.campaignReferenceImage.hidden = true;
        }
      }
      this.campaignReferencePanel.removeAttribute('hidden');
      this.campaignReferencePanel.hidden = false;
      return true;
    }

    updateCampaignVisuals() {
      if (!this.isCampaign) return;
      const selectedColorRaw = String(this.campaignColor?.value || '').trim();
      const selectedFloorRaw = String(this.campaignFloor?.value || '').trim();
      const selectedColor = this.normalizeCampaignColor(selectedColorRaw);
      const selectedFloor = this.normalizeCampaignText(selectedFloorRaw);
      const currentColor = this.normalizeCampaignColor(this.currentCampaignColor);
      const currentFloor = this.normalizeCampaignText(this.currentCampaignFloor);
      const labelBase = [this.currentCampaignModel, selectedColorRaw, selectedFloorRaw].filter(Boolean).join(' · ');

      this.restoreMainCampaignGallery();
      const gallery = this.mainCampaignGallery;
      const media = gallery ? Array.from(gallery.querySelectorAll('[data-b2b-campaign-media]')) : [];
      const currentCombination = Boolean(selectedColor)
        && selectedColor === currentColor
        && (!selectedFloor || !currentFloor || selectedFloor === currentFloor);

      this.activeCampaignReference = null;
      if (this.campaignReferencePanel) {
        this.campaignReferencePanel.hidden = true;
        this.campaignReferencePanel.setAttribute('hidden', '');
      }
      if (this.campaignReferenceLink) this.campaignReferenceLink.hidden = true;
      if (this.campaignReferenceImage) {
        this.campaignReferenceImage.hidden = true;
        this.campaignReferenceImage.removeAttribute('src');
      }
      if (this.campaignReferenceMessage) this.campaignReferenceMessage.textContent = '';
      if (this.campaignColorNote) this.campaignColorNote.hidden = true;

      if (!selectedColorRaw || currentCombination) {
        media.forEach((item) => { item.hidden = false; });
        this.setCampaignCompactGallery(
          this.originalCampaignImages,
          [this.currentCampaignModel, this.currentCampaignColor, this.currentCampaignFloor].filter(Boolean).join(' · '),
          'Fotos reales de esta muestra y de esta combinación.',
          false
        );
        this.updateCampaignSampleHighlight();
        return;
      }

      const localMatches = media.filter((item) => {
        const alt = this.normalizeCampaignText(item.dataset.b2bMediaAlt);
        return alt.includes(selectedColor) && (!selectedFloor || alt.includes(selectedFloor));
      });

      if (localMatches.length) {
        media.forEach((item) => { item.hidden = !localMatches.includes(item); });
        const localImages = localMatches.map((item) => {
          const img = item.querySelector('img');
          return { src: img?.currentSrc || img?.src || '', alt: img?.alt || labelBase };
        }).filter((item) => item.src);
        this.setCampaignCompactGallery(localImages, labelBase, 'Fotos reales de la combinación seleccionada.', false);
        this.updateCampaignSampleHighlight();
        return;
      }

      const match = this.campaignReferenceMatch(selectedColorRaw, selectedFloorRaw);
      const reference = match.reference;
      if (reference && match.kind === 'exact') {
        const images = reference.images?.length ? reference.images : [{ src: reference.image, alt: reference.title }].filter((item) => item.src);
        this.setCampaignCompactGallery(images, labelBase, 'Fotos reales de esta combinación en el mismo modelo que estás comprando.', false);
        this.renderExactReferenceInMainGallery(images, labelBase);
        if (this.campaignReferencePanel) {
          this.campaignReferencePanel.hidden = true;
          this.campaignReferencePanel.setAttribute('hidden', '');
        }
      } else if (reference) {
        const images = reference.images?.length ? reference.images : [{ src: reference.image, alt: reference.title }].filter((item) => item.src);
        let note = '';
        if (match.kind === 'same-model-other-floor') {
          note = `Referencia visual real: ${selectedColorRaw} está fotografiado en el modelo ${this.currentCampaignModel} con ${reference.floor || 'otro piso'}. Tu pedido sigue siendo ${labelBase}.`;
        } else if (match.kind === 'other-model-same-floor') {
          note = `Referencia visual real: ${selectedColorRaw} con ${reference.floor || selectedFloorRaw} está fotografiado en el modelo ${reference.model || 'de referencia'} de la misma sección. Tu pedido sigue siendo ${labelBase}.`;
        } else {
          note = `Referencia visual real: la muestra disponible es ${selectedColorRaw}${reference.floor ? ` con ${reference.floor}` : ''} en el modelo ${reference.model || 'de referencia'}. Tu pedido sigue siendo ${labelBase}.`;
        }
        this.setCampaignCompactGallery(images, `Referencia · ${reference.model || ''} · ${selectedColorRaw}${reference.floor ? ` · ${reference.floor}` : ''}`, note, true);
        this.showCampaignReference(reference, selectedColorRaw, selectedFloorRaw);
      } else {
        this.setCampaignCompactGallery(
          this.originalCampaignImages,
          labelBase,
          `Disponible en carta. No tenemos una muestra real publicada de ${selectedColorRaw}${selectedFloorRaw ? ` con ${selectedFloorRaw}` : ''} en esta sección. Las fotos mostradas son de la muestra original del modelo ${this.currentCampaignModel}.`,
          true
        );
        if (this.campaignColorNote) {
          const section = this.campaignSection ? ` en la sección ${this.campaignSection}` : '';
          this.campaignColorNote.textContent = `${selectedColorRaw} está disponible para campaña, pero todavía no tenemos una muestra real publicada de este acabado${section}. El pedido seguirá siendo ${labelBase}.`;
          this.campaignColorNote.hidden = false;
        }
      }
      this.updateCampaignSampleHighlight();
    }

    campaignFixedFloorForColor(colorValue) {
      const color = this.normalizeCampaignText(colorValue);
      if (!color || !this.campaignCombinations?.length) return '';
      for (const combo of this.campaignCombinations) {
        const [comboColorRaw, comboFloorRaw] = String(combo || '').split('|');
        if (this.normalizeCampaignText(comboColorRaw) === color) {
          return String(comboFloorRaw || '').trim();
        }
      }
      return '';
    }

    syncCampaignFixedFloor() {
      if (!this.isCampaign || !this.campaignFloor) return false;
      const color = String(this.campaignColor?.value || '').trim();
      const fixedFloor = this.campaignFixedFloorForColor(color);
      if (!fixedFloor) return false;
      this.campaignFloor.value = fixedFloor;
      if (this.campaignFloorLabel) this.campaignFloorLabel.textContent = fixedFloor;
      return true;
    }

    currentCampaignPriceMatch() {
      if (!this.isCampaign) return { reference: null, kind: 'none' };
      const color = String(this.campaignColor?.value || '').trim();
      const floor = String(this.campaignFloor?.value || '').trim();
      if (!color) return { reference: null, kind: 'none' };
      return this.campaignReferenceMatch(color, floor);
    }

    isCurrentCampaignSelectionVirtual() {
      if (!this.isCampaign) return false;
      const selectedColor = this.normalizeCampaignText(this.campaignColor?.value || '');
      const selectedFloor = this.normalizeCampaignText(this.campaignFloor?.value || '');
      const currentColor = this.normalizeCampaignText(this.currentCampaignColor || '');
      const currentFloor = this.normalizeCampaignText(this.currentCampaignFloor || '');
      if (selectedColor === currentColor && (!selectedFloor || !currentFloor || selectedFloor === currentFloor)) return false;

      const match = this.currentCampaignPriceMatch();
      return !(match?.reference
        && match.kind === 'exact'
        && this.normalizeCampaignText(match.reference.model) === this.normalizeCampaignText(this.currentCampaignModel));
    }

    updateCampaignPricing() {
      if (!this.isCampaign) return;

      // CARACAS y cualquier perfil fijo: el piso manda la tabla de combinaciones.
      this.syncCampaignFixedFloor();

      const visualMatch = this.currentCampaignPriceMatch();
      const priceMatch = this.campaignPriceReferenceMatch(
        String(this.campaignColor?.value || '').trim(),
        String(this.campaignFloor?.value || '').trim()
      );
      this.activeCampaignPriceReference = priceMatch?.reference || null;
      this.activeCampaignMatchKind = priceMatch?.kind || 'none';

      const useExactVariant = Boolean(
        visualMatch?.reference
        && visualMatch.kind === 'exact'
        && this.normalizeCampaignText(visualMatch.reference.model) === this.normalizeCampaignText(this.currentCampaignModel)
      );

      this.inputs.forEach((input) => {
        const size = this.normalizeCampaignText(input.dataset.size || '');
        const sourceVariant = priceMatch?.reference?.variants?.[size] || null;
        const basePrice = Math.max(0, toInt(input.dataset.basePrice || input.dataset.price, 0));
        const referenceDefaultPrice = Math.max(0, toInt(priceMatch?.reference?.defaultPrice, 0));
        const campaignPrice = sourceVariant?.price > 0 ? sourceVariant.price : (referenceDefaultPrice > 0 ? referenceDefaultPrice : basePrice);
        input.dataset.price = String(campaignPrice);
        const visualSourceVariant = visualMatch?.reference?.variants?.[size] || null;
        input.dataset.effectiveVariantId = useExactVariant && visualSourceVariant?.id
          ? String(visualSourceVariant.id).replace(/\D/g, '')
          : String(input.dataset.baseVariantId || input.dataset.variantId || '');
        input.dataset.campaignPriceSourceVariantId = sourceVariant?.id
          ? String(sourceVariant.id)
          : '';
        input.dataset.campaignVirtual = this.isCurrentCampaignSelectionVirtual() ? '1' : '0';

        const card = input.closest('[data-b2b-variant]');
        const label = card?.querySelector('[data-b2b-price-label]');
        if (label) {
          label.innerHTML = `${this.formatMoney.format(campaignPrice / 100)} <small>sin IVA</small>`;
        }
      });

      this.updateSummary();
    }

    normalizeInput(input) {
      let value = Math.max(0, toInt(input.value, 0));
      const min = Math.max(1, toInt(input.dataset.min, 1));
      const increment = Math.max(1, toInt(input.dataset.increment, 1));
      const maxRaw = toInt(input.dataset.max, 0);
      const max = maxRaw > 0 ? maxRaw : null;

      if (value > 0 && value < min) value = min;
      if (value > 0) {
        const offset = Math.max(0, value - min);
        value = min + Math.round(offset / increment) * increment;
      }
      if (max !== null) value = Math.min(value, max);

      input.value = String(value);
      return value;
    }

    onInput(event) {
      const input = event.target.closest('[data-b2b-qty]');
      if (!input) return;
      this.normalizeInput(input);
      this.updateSummary();
    }

    onClick(event) {
      const sampleSelect = event.target.closest('[data-b2b-campaign-sample-select]');
      if (sampleSelect) {
        event.preventDefault();

        const color = String(sampleSelect.dataset.sampleColor || '').trim();
        const floor = String(sampleSelect.dataset.sampleFloor || '').trim();
        const sampleUrl = String(sampleSelect.dataset.sampleUrl || '').trim();

        // Si existe un producto real del MISMO MODELO para este color, abrimos
        // su ficha completa. No reciclamos la ficha actual ni mezclamos galerías.
        if (sampleUrl) {
          const target = new URL(sampleUrl, window.location.origin);
          const targetPath = target.pathname.replace(/\/$/, '');
          const currentPath = window.location.pathname.replace(/\/$/, '');
          const sampleModel = this.normalizeCampaignText(sampleSelect.dataset.sampleModel || this.currentCampaignModel || '');
          const currentModel = this.normalizeCampaignText(this.currentCampaignModel || '');
          if (targetPath && targetPath !== currentPath && (!sampleModel || !currentModel || sampleModel === currentModel)) {
            window.location.assign(target.href);
            return;
          }
        }

        if (color && this.campaignColor) {
          const option = Array.from(this.campaignColor.options || []).find(
            (item) => this.normalizeCampaignText(item.value || item.textContent || '') === this.normalizeCampaignText(color)
          );
          if (option) this.campaignColor.value = option.value;
        }

        if (floor && this.campaignFloor) {
          const option = Array.from(this.campaignFloor.options || []).find(
            (item) => this.normalizeCampaignText(item.value || item.textContent || '') === this.normalizeCampaignText(floor)
          );
          if (option) this.campaignFloor.value = option.value;
        }

        this.clearStatus();
        this.syncCampaignFixedFloor();
        this.updateCampaignPricing();
        this.updateCampaignVisuals();
        this.updateCampaignSampleHighlight();
        this.refreshCampaignReferenceFromServer().catch(() => {});
        return;
      }

      const referenceGalleryOpen = event.target.closest('[data-b2b-reference-gallery-open]');
      if (referenceGalleryOpen && this.activeCampaignReference) {
        event.preventDefault();
        const images = this.activeCampaignReference.images?.length
          ? this.activeCampaignReference.images
          : [{ src: this.activeCampaignReference.image, alt: this.activeCampaignReference.title }].filter((item) => item.src);
        this.openCampaignLightboxGallery(
          images,
          `${this.activeCampaignReference.model || ''} · ${this.activeCampaignReference.color || ''}${this.activeCampaignReference.floor ? ` · ${this.activeCampaignReference.floor}` : ''}`.trim(),
          0
        );
        return;
      }

      const lightboxPrev = event.target.closest('[data-b2b-campaign-lightbox-prev]');
      if (lightboxPrev) {
        event.preventDefault();
        this.stepCampaignLightbox(-1);
        return;
      }

      const lightboxNext = event.target.closest('[data-b2b-campaign-lightbox-next]');
      if (lightboxNext) {
        event.preventDefault();
        this.stepCampaignLightbox(1);
        return;
      }

      const closeLightbox = event.target.closest('[data-b2b-campaign-lightbox-close]');
      if (closeLightbox) {
        event.preventDefault();
        this.closeCampaignLightbox();
        return;
      }

      const campaignThumb = event.target.closest('[data-b2b-campaign-thumb]');
      if (campaignThumb) {
        event.preventDefault();
        this.setCampaignMainImage({
          src: String(campaignThumb.dataset.imageUrl || ''),
          alt: String(campaignThumb.dataset.imageAlt || ''),
        });
        return;
      }

      const campaignMain = event.target.closest('[data-b2b-campaign-main-image-button]');
      if (campaignMain) {
        event.preventDefault();
        this.openCampaignLightbox();
        return;
      }

      const stepButton = event.target.closest('[data-b2b-step]');
      if (stepButton) {
        const wrapper = stepButton.closest('[data-b2b-variant]');
        const input = wrapper?.querySelector('[data-b2b-qty]');
        if (!input) return;

        const direction = stepButton.dataset.b2bStep;
        const current = toInt(input.value, 0);
        const min = Math.max(1, toInt(input.dataset.min, 1));
        const increment = Math.max(1, toInt(input.dataset.increment, 1));
        const maxRaw = toInt(input.dataset.max, 0);
        const max = maxRaw > 0 ? maxRaw : null;

        let next;
        if (direction === 'plus') {
          next = current === 0 ? min : current + increment;
          if (max !== null) next = Math.min(next, max);
        } else {
          next = current <= min ? 0 : Math.max(0, current - increment);
        }

        input.value = String(next);
        this.updateSummary();
        return;
      }

      const preset = event.target.closest('[data-b2b-preset]');
      if (!preset) return;
      if (preset.dataset.b2bPreset === 'one') {
        this.inputs.forEach((input) => {
          if (!input.disabled) input.value = String(Math.max(1, toInt(input.dataset.min, 1)));
        });
      } else if (preset.dataset.b2bPreset === 'clear') {
        this.inputs.forEach((input) => { input.value = '0'; });
      }
      this.updateSummary();
    }

    updateSummary() {
      let pairs = 0;
      let cents = 0;

      this.inputs.forEach((input) => {
        const quantity = Math.max(0, toInt(input.value, 0));
        const price = Math.max(0, toInt(input.dataset.price, 0));
        pairs += quantity;
        cents += quantity * price;
      });

      if (this.totalPairs) this.totalPairs.textContent = String(pairs);
      if (this.totalAmount) this.totalAmount.textContent = this.formatMoney.format(cents / 100);
      if (this.addButton) {
        let campaignReady = true;
        let blockReason = '';
        if (this.isCampaign) {
          const colorReady = !this.campaignColor || String(this.campaignColor.value || '').trim() !== '';
          const floorReady = !this.campaignFloor || String(this.campaignFloor.value || '').trim() !== '';
          campaignReady = colorReady && floorReady;
          if (!colorReady) blockReason = 'Selecciona primero la piel / color.';
          else if (!floorReady) blockReason = 'Selecciona primero el piso.';
        }
        const mixedModeBlocked = (this.isCampaign && this.cartHasStandard) || (!this.isCampaign && this.cartHasCampaign);
        const campaignStockBlocked = this.isCampaign && this.productPositiveStock;
        if (mixedModeBlocked) {
          blockReason = this.isCampaign
            ? 'Tu carrito contiene reposición. Envíalo o vacíalo antes de añadir artículos de campaña.'
            : 'Tu carrito contiene campaña. Envíalo o vacíalo antes de añadir reposición.';
        } else if (campaignStockBlocked) {
          blockReason = 'Este artículo tiene stock y pertenece a venta actual; no puede añadirse a campaña.';
        }
        this.addButton.disabled = pairs === 0 || !campaignReady || mixedModeBlocked || campaignStockBlocked;
        this.addButton.title = blockReason;
        if (this.addBlockReason) {
          this.addBlockReason.textContent = pairs > 0 && blockReason ? blockReason : '';
          this.addBlockReason.hidden = !(pairs > 0 && blockReason);
        }
      }

      if (this.shippingPreview) {
        const currentCartPairs = Math.max(0, toInt(this.dataset.cartPairs, 0));
        const projected = currentCartPairs + pairs;
        if (projected >= FREE_SHIPPING_THRESHOLD) {
          this.shippingPreview.textContent = `✓ Con esta selección llegarás a ${projected} pares: PORTES GRATIS.`;
          this.shippingPreview.classList.add('is-free');
        } else {
          const remaining = FREE_SHIPPING_THRESHOLD - projected;
          this.shippingPreview.textContent = `Con esta selección tendrás ${projected} pares. Te faltarán ${remaining} para portes gratis.`;
          this.shippingPreview.classList.remove('is-free');
        }
      }

      this.clearStatus();
    }

    clearStatus() {
      if (!this.status) return;
      this.status.textContent = '';
      this.status.classList.remove('clayan-b2b-order__status--error');
    }

    setStatus(message, error = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('clayan-b2b-order__status--error', error);
    }

    async addToCart() {
      if (this.isCampaign && this.productPositiveStock) {
        this.setStatus('Este artículo tiene stock y pertenece a venta actual. No puede añadirse al pedido de campaña.', true);
        return;
      }
      if (this.isCampaign && this.cartHasStandard) {
        this.setStatus('Tu carrito contiene artículos de reposición. Envíalos o vacía el carrito antes de empezar un pedido de campaña.', true);
        return;
      }
      if (!this.isCampaign && this.cartHasCampaign) {
        this.setStatus('Tu carrito contiene artículos de campaña. Envíalos o vacía el carrito antes de añadir reposición.', true);
        return;
      }
      const campaignProperties = this.campaignProperties();
      if (this.isCampaign && campaignProperties === null) return;

      const items = this.inputs
        .map((input) => {
          const item = {
            id: toInt(input.dataset.effectiveVariantId || input.dataset.variantId, 0),
            quantity: Math.max(0, toInt(input.value, 0)),
          };
          if (this.isCampaign) {
            item.properties = {
              ...campaignProperties,
              '_Campaña precio céntimos': String(Math.max(0, toInt(input.dataset.price, 0))),
              '_Campaña precio fuente variante': String(input.dataset.campaignPriceSourceVariantId || ''),
              '_Campaña virtual': String(input.dataset.campaignVirtual || '0'),
            };
          }
          return item;
        })
        .filter((item) => item.id && item.quantity > 0);

      if (!items.length || !this.addButton) return;

      const originalText = this.addButton.textContent;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.textContent = 'Añadiendo…';
      this.setStatus('');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const payload = { items };

      if (cart && typeof cart.getSectionsToRender === 'function') {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        if (typeof cart.setActiveElement === 'function') cart.setActiveElement(this.addButton);
      }

      try {
        const response = await fetch(`${this.dataset.cartAddUrl}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'No se pudo añadir el pedido al carrito.');
        }

        if (cart && typeof cart.renderContents === 'function' && data.sections) {
          if (cart.classList?.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(data);
        }

        if (window.publish && window.PUB_SUB_EVENTS?.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'clayan-b2b-order',
            cartData: data,
          });
        }

        const addedQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

        items.forEach((item) => {
          const input = this.inputs.find((candidate) =>
            toInt(candidate.dataset.effectiveVariantId || candidate.dataset.variantId, 0) === item.id
          );
          if (!input) return;
          if (input.dataset.stockTracked === 'true') {
            const oldMax = toInt(input.dataset.max, 0);
            const nextMax = Math.max(0, oldMax - item.quantity);
            input.dataset.max = String(nextMax);
            input.max = String(nextMax);
            const card = input.closest('[data-b2b-variant]');
            const stock = card?.querySelector('[data-b2b-stock] strong');
            if (stock) stock.textContent = String(nextMax);
            if (nextMax <= 0) {
              input.disabled = true;
              card?.classList.add('clayan-b2b-variant--soldout');
            }
          }
          input.value = '0';
        });

        this.setStatus(`${addedQuantity} pares añadidos al carrito.`);
        await refreshCartMeta();
        this.updateSummary();

        if (!cart) window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        this.setStatus(error.message || 'No se pudo añadir el pedido al carrito.', true);
      } finally {
        this.addButton.removeAttribute('aria-busy');
        this.addButton.textContent = originalText;
        this.addButton.disabled = this.inputs.every((input) => toInt(input.value, 0) === 0);
      }
    }
  }

  if (!customElements.get('clayan-b2b-order')) {
    customElements.define('clayan-b2b-order', ClayanB2BOrder);
  }

  const initialShippingElement = document.querySelector('[data-clayan-b2b-shipping-status]');
  if (initialShippingElement) {
    updateShippingUI(toInt(initialShippingElement.dataset.currentPairs, 0));
  }

  if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
      const count = event?.cartData?.item_count;
      if (Number.isFinite(Number(count))) updateShippingUI(Number(count));
      else refreshCartMeta();
    });
  } else if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
    subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
      const count = event?.cartData?.item_count;
      if (Number.isFinite(Number(count))) updateShippingUI(Number(count));
      else refreshCartMeta();
    });
  }


  const bindContinueShopping = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-clayan-b2b-continue-shopping]');
      if (!button) return;
      event.preventDefault();
      let targetUrl = String(button.dataset.clayanB2bContinueShoppingUrl || '').trim();
      const forceCampaign = button.closest('[data-clayan-b2b-cart-campaign="true"]')
        || document.querySelector('[data-clayan-b2b-cart-campaign="true"]');
      if (forceCampaign) targetUrl = '/collections/compra-campana-verano-27';

      // Si Liquid no ha podido clasificar el carrito (por ejemplo tras una
      // actualización AJAX), comprobamos las propiedades reales del carrito.
      // Así “Seguir comprando” nunca saca una campaña al catálogo general.
      if (!targetUrl) {
        event.stopPropagation();
        fetchCurrentCart().then((cart) => {
          const items = Array.isArray(cart?.items) ? cart.items : [];
          const campaignOnly = items.length > 0 && items.every((item) => {
            const props = item?.properties && typeof item.properties === 'object' ? item.properties : {};
            return Boolean(String(props['Campaña'] || props['Campana'] || props['_Campaña sección'] || '').trim());
          });
          if (campaignOnly) {
            window.location.assign('/collections/compra-campana-verano-27');
            return;
          }
          const drawer = button.closest('cart-drawer') || document.querySelector('cart-drawer');
          if (drawer && typeof drawer.close === 'function') drawer.close();
          else drawer?.querySelector('.drawer__close')?.click();
        }).catch(() => {
          const drawer = button.closest('cart-drawer') || document.querySelector('cart-drawer');
          if (drawer && typeof drawer.close === 'function') drawer.close();
          else drawer?.querySelector('.drawer__close')?.click();
        });
        return;
      }
      if (targetUrl) {
        window.location.assign(targetUrl);
        return;
      }
      const drawer = button.closest('cart-drawer') || document.querySelector('cart-drawer');
      if (drawer && typeof drawer.close === 'function') {
        drawer.close();
        return;
      }
      const closeButton = drawer?.querySelector('.drawer__close');
      if (closeButton) closeButton.click();
    });
  };

  bindContinueShopping();

  document.addEventListener('cart:updated', refreshCartMeta);
  document.addEventListener('DOMContentLoaded', () => {
    const el = document.querySelector('[data-clayan-b2b-shipping-status]');
    if (el) updateShippingUI(toInt(el.dataset.currentPairs, 0));
  });


  class ClayanB2BFinalOrder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.serverReady === 'true') return;
      this.dataset.serverReady = 'true';

      this.endpoint = (this.dataset.endpoint || '/apps/clayan-b2b').replace(/\/$/, '');
      this.currency = this.dataset.currency || 'EUR';
      this.formatMoney = moneyFormatter(this.currency);
      this.subtotalCents = Math.max(0, toInt(this.dataset.cartSubtotalCents, 0));
      this.pairs = Math.max(0, toInt(this.dataset.cartPairs, 0));
      this.hasRE = this.dataset.hasRe === 'true';
      this.serviceReady = false;
      // No permitir dobles envíos ni reintentos cuando el resultado sea incierto.
      this.orderSending = false;
      this.orderOutcomeUncertain = false;
      this.orderConfirmed = false;
      this.context = null;
      this.preview = null;
      this.habitualCents = 0;
      this.habitualPercent = 0;
      this.originalCents = this.subtotalCents;
      this.merchandiseCents = this.subtotalCents;
      this.submitButton = this.querySelector('[data-b2b-submit-order]');
      this.confirm = this.querySelector('[data-b2b-confirm]');
      this.message = this.querySelector('[data-b2b-order-message]');
      this.serviceStatus = this.querySelector('[data-b2b-service-status]');
      this.success = this.querySelector('[data-b2b-success]');
      this.isCampaignOrder = this.dataset.hasCampaignOrder === 'true';
      this.hasStandardOrder = this.dataset.hasStandardOrder === 'true';
      this.mixedOrder = this.dataset.mixedOrder === 'true';
      this.globalFortnight = this.querySelector('[data-b2b-global-fortnight]');

      const configNode = this.querySelector('[data-b2b-order-config]');
      try { this.config = JSON.parse(configNode?.textContent || '{}'); }
      catch (error) { this.config = {}; }
      this.habitualPercent = Number(this.config?.habitual_discount_percent || 0) || 0;

      try {
        this.requestId = sessionStorage.getItem('clayanB2BOrderRequestId');
        if (!this.requestId) {
          this.requestId = window.crypto?.randomUUID?.() || `clayan-${Date.now()}-${Math.random().toString(36).slice(2)}`;
          sessionStorage.setItem('clayanB2BOrderRequestId', this.requestId);
        }
      } catch (error) {
        this.requestId = window.crypto?.randomUUID?.() || `clayan-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      }

      this.addEventListener('change', this.onChange.bind(this));
      this.submitButton?.addEventListener('click', this.submitOrder.bind(this));
      this.globalFortnight?.addEventListener('change', () => {
        this.setMessage('');
        this.updateSubmitAvailability();
      });
      this.updateLocalTotals();
      this.updateSubmitAvailability();
      if (this.mixedOrder) {
        this.setServiceStatus('Separa campaña y reposición', 'error');
        this.setMessage('No se pueden mezclar artículos de campaña y reposición en el mismo pedido.', true);
      } else {
        this.loadContextAndPreview();
      }
    }

    currentPaymentMode() {
      return this.querySelector('[data-b2b-payment-option]:checked')?.value || 'habitual';
    }

    setServiceStatus(text, state = '') {
      if (!this.serviceStatus) return;
      this.serviceStatus.textContent = text;
      this.serviceStatus.dataset.state = state;
    }

    setMessage(text = '', error = false) {
      if (!this.message) return;
      this.message.textContent = text;
      this.message.classList.toggle('is-error', error);
      this.message.classList.toggle('is-success', Boolean(text) && !error);
    }

    money(cents) {
      return this.formatMoney.format((Number(cents) || 0) / 100);
    }

    setMoney(selector, cents, prefix = '') {
      const el = this.querySelector(selector);
      if (el) el.textContent = `${prefix}${this.money(cents)}`;
    }

    updateLocalTotals() {
      const early = this.currentPaymentMode() === 'pronto_pago';
      const merchandiseCents = Math.max(0, Number(this.merchandiseCents ?? this.subtotalCents) || 0);
      const earlyCents = early ? Math.round(merchandiseCents * 0.03) : 0;
      const shippingCents = this.pairs >= FREE_SHIPPING_THRESHOLD ? 0 : 1000;
      const baseCents = Math.max(0, merchandiseCents - earlyCents + shippingCents);
      const ivaCents = Math.round(baseCents * 0.21);
      const reCents = this.hasRE ? Math.round(baseCents * 0.052) : 0;
      const totalCents = baseCents + ivaCents + reCents;

      this.calculation = { earlyCents, shippingCents, baseCents, ivaCents, reCents, totalCents };

      const earlyRow = this.querySelector('[data-b2b-early-row]');
      if (earlyRow) earlyRow.hidden = !early;
      this.setMoney('[data-b2b-merchandise]', merchandiseCents);
      this.setMoney('[data-b2b-early-discount]', earlyCents, '−');
      const shipping = this.querySelector('[data-b2b-shipping-amount]');
      if (shipping) shipping.textContent = shippingCents === 0 ? 'GRATIS' : this.money(shippingCents);
      this.setMoney('[data-b2b-tax-base]', baseCents);
      this.setMoney('[data-b2b-iva]', ivaCents);
      const reRow = this.querySelector('[data-b2b-re-row]');
      if (reRow) reRow.hidden = !this.hasRE;
      this.setMoney('[data-b2b-re]', reCents);
      this.setMoney('[data-b2b-grand-total]', totalCents);

      this.querySelectorAll('.clayan-b2b-payment-option').forEach((option) => {
        option.classList.toggle('is-selected', option.querySelector('input')?.checked === true);
      });
    }

    onChange(event) {
      if (event.target.matches('[data-b2b-global-fortnight]')) {
        this.setMessage('');
        this.updateSubmitAvailability();
        return;
      }
      if (event.target.matches('[data-b2b-payment-option]')) {
        this.updateLocalTotals();
        if (this.serviceReady) this.refreshPreview().catch(() => {});
      }
    }

    globalFortnightValue() {
      return String(this.globalFortnight?.value || '').trim();
    }

    updateSubmitAvailability() {
      if (!this.submitButton) return;
      const missingFortnight = this.isCampaignOrder && !this.globalFortnightValue();
      this.submitButton.disabled = this.pairs <= 0 || missingFortnight || this.mixedOrder
        || !this.serviceReady || this.orderSending || this.orderOutcomeUncertain || this.orderConfirmed;
      this.submitButton.title = this.mixedOrder
        ? 'Separa campaña y reposición antes de enviar el pedido'
        : (missingFortnight ? 'Selecciona la quincena general de entrega' : '');
      const block = this.querySelector('[data-b2b-global-delivery]');
      block?.classList.toggle('is-missing', missingFortnight);
    }

    combinedOrderNote() {
      const note = this.querySelector('[data-b2b-order-note]')?.value.trim() || '';
      if (!this.isCampaignOrder) return note;
      const fortnight = this.globalFortnightValue();
      const parts = [];
      if (fortnight) parts.push(`QUINCENA GENERAL DE ENTREGA: ${fortnight}`);
      if (note) parts.push(`OBSERVACIONES: ${note}`);
      return parts.join('\n\n');
    }

    fieldValue(name) {
      return this.querySelector(`[data-b2b-shipping-field="${name}"]`)?.value?.trim() || '';
    }

    addressLine() {
      const parts = [
        this.fieldValue('company'),
        this.fieldValue('address1'),
        this.fieldValue('address2'),
        [this.fieldValue('zip'), this.fieldValue('city')].filter(Boolean).join(' '),
        this.fieldValue('province'),
        this.fieldValue('country'),
      ].filter(Boolean);
      return parts.join('\n');
    }

    text(selector, fallback = '—') {
      const value = this.querySelector(selector)?.textContent?.trim();
      return value || fallback;
    }

    splitModelDescription(title = '') {
      const clean = String(title || '').replace(/\s+/g, ' ').trim();
      const numeric = clean.match(/\b(\d{1,5}[A-Za-z]?)\b/);
      if (numeric) {
        const model = numeric[1];
        const description = `${clean.slice(0, numeric.index)} ${clean.slice((numeric.index || 0) + numeric[0].length)}`
          .replace(/\s+/g, ' ').trim();
        return { model, description: description || clean };
      }
      const parts = clean.split(' ').filter(Boolean);
      const model = parts.shift() || '—';
      return { model, description: parts.join(' ') || clean || '—' };
    }

    variantSize(variantTitle = '') {
      const match = String(variantTitle || '').match(/(?:^|\D)(3[5-9]|4[0-9])(?:\D|$)/);
      return match ? Number(match[1]) : null;
    }

    itemProperty(item, name) {
      const properties = item?.properties;
      if (!properties) return '';
      if (Array.isArray(properties)) {
        const pair = properties.find((entry) => Array.isArray(entry) && entry[0] === name);
        return pair ? String(pair[1] || '').trim() : '';
      }
      return String(properties[name] || '').trim();
    }

    groupedOrderRows() {
      const items = Array.isArray(this.config?.items) ? this.config.items : [];
      const groups = new Map();

      items.forEach((item) => {
        const parsed = this.splitModelDescription(item.title || 'Artículo');
        const campaignColor = this.itemProperty(item, 'Piel / Color');
        const campaignFloor = this.itemProperty(item, 'Piso');
        const campaignDetails = [campaignColor, campaignFloor].filter(Boolean);
        // En campaña el documento muestra exclusivamente lo que el cliente eligió.
        // El título técnico/base del producto no se mezcla con la selección.
        const description = campaignDetails.length
          ? campaignDetails.join(' · ')
          : parsed.description;
        const key = `${parsed.model}|||${description}`;
        if (!groups.has(key)) {
          groups.set(key, {
            model: parsed.model,
            description,
            sizes: Object.fromEntries(Array.from({ length: 15 }, (_, i) => [35 + i, 0])),
            other: 0,
            total: 0,
          });
        }
        const row = groups.get(key);
        const qty = Math.max(0, Number(item.quantity) || 0);
        const size = this.variantSize(item.variant_title);
        if (size && size >= 35 && size <= 49) row.sizes[size] += qty;
        else row.other += qty;
        row.total += qty;
      });

      return Array.from(groups.values());
    }


    orderSnapshot() {
      const mode = this.currentPaymentMode();
      return {
        savedAt: new Date().toISOString(),
        dateText: new Date().toLocaleString('es-ES'),
        currency: this.currency,
        company: this.context?.company?.name || this.config?.company_name || this.text('[data-b2b-company-name]'),
        contact: this.context?.contacto_empresa || this.config?.contact_name || this.config?.customer_name || '—',
        email: this.context?.customer?.email || this.config?.customer_email || '—',
        taxId: this.context?.cif_dni || this.context?.tax_registration_id || this.config?.tax_id || this.text('[data-b2b-tax-id]'),
        agent: this.context?.agente_comercial || this.config?.agent_name || '—',
        phone: this.fieldValue('phone') || '—',
        shipping: this.addressLine() || '—',
        payment: mode === 'pronto_pago' ? 'Transferencia · pronto pago −3 %' : 'Mis condiciones habituales',
        reference: this.querySelector('[data-b2b-po-number]')?.value.trim() || '—',
        deliveryFortnight: this.isCampaignOrder ? (this.globalFortnightValue() || '—') : '',
        note: this.querySelector('[data-b2b-order-note]')?.value.trim() || '—',
        rows: this.groupedOrderRows(),
        totalPairs: this.pairs,
        totals: {
          original: this.text('[data-b2b-original-merchandise]', this.money(this.originalCents ?? this.subtotalCents)),
          habitual: (this.habitualCents || 0) > 0 ? this.text('[data-b2b-habitual-discount]', '') : '',
          habitualLabel: (this.habitualCents || 0) > 0 ? this.text('[data-b2b-habitual-label]', 'Descuento habitual') : '',
          hasHabitual: (this.habitualCents || 0) > 0,
          merchandise: this.money(this.merchandiseCents ?? this.subtotalCents),
          early: mode === 'pronto_pago' ? `−${this.money(this.calculation?.earlyCents || 0)}` : 'No aplicado',
          shipping: (this.calculation?.shippingCents || 0) === 0 ? 'GRATIS' : this.money(this.calculation?.shippingCents || 0),
          base: this.money(this.calculation?.baseCents || 0),
          iva: this.money(this.calculation?.ivaCents || 0),
          re: this.hasRE ? this.money(this.calculation?.reCents || 0) : 'No aplica',
          total: this.money(this.calculation?.totalCents || 0),
        },
      };
    }

    saveOrderSnapshot() {
      try {
        sessionStorage.setItem('clayan_b2b_last_order_visual_v1', JSON.stringify(this.orderSnapshot()));
      } catch (error) {}
    }



    proxyUrl(path) {
      const locationId = encodeURIComponent(this.config?.company_location_id || this.dataset.companyLocationId || '');
      return `${this.endpoint}${path}?company_location_id=${locationId}`;
    }

    async fetchJSON(url, options = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'ngrok-skip-browser-warning': '1',
          ...(options.body ? { 'Content-Type': 'application/json' } : {}),
          ...(options.headers || {}),
        },
        ...options,
      });
      let data = null;
      try { data = await response.json(); } catch (error) { data = null; }
      if (!response.ok) {
        const message = data?.detail || data?.error || data?.message || `Error ${response.status}`;
        throw new Error(message);
      }
      return data || {};
    }

    setShippingField(name, value) {
      const input = this.querySelector(`[data-b2b-shipping-field="${name}"]`);
      if (input && value != null) input.value = value;
    }

    applyContext(ctx) {
      this.context = ctx || {};
      if (ctx?.company?.name) {
        const company = this.querySelector('[data-b2b-company-name]');
        if (company) company.textContent = ctx.company.name;
      }
      const tax = this.querySelector('[data-b2b-tax-id]');
      if (tax) tax.textContent = ctx?.cif_dni || ctx?.tax_registration_id || '—';

      const contact = this.querySelector('[data-b2b-contact-name]');
      if (contact) contact.textContent = ctx?.contacto_empresa || ctx?.customer?.name || this.config?.customer_name || '—';

      const customerEmail = this.querySelector('[data-b2b-customer-email]');
      if (customerEmail && ctx?.customer?.email) customerEmail.textContent = ctx.customer.email;

      const agentRow = this.querySelector('[data-b2b-agent-row]');
      const agent = this.querySelector('[data-b2b-agent-name]');
      const agentName = ctx?.agente_comercial || '';
      if (agent) agent.textContent = agentName || '—';
      if (agentRow) agentRow.hidden = !agentName;

      this.habitualPercent = Number(ctx?.descuento_habitual_percent || 0) || 0;

      this.hasRE = Boolean(ctx?.recargo_equivalencia);
      this.dataset.hasRe = this.hasRE ? 'true' : 'false';

      const shipping = ctx?.shipping_address || {};
      this.setShippingField('company', shipping.company || ctx?.company?.name || '');
      this.setShippingField('phone', shipping.phone || ctx?.phone || '');
      this.setShippingField('address1', shipping.address1 || '');
      this.setShippingField('address2', shipping.address2 || '');
      this.setShippingField('zip', shipping.zip || '');
      this.setShippingField('city', shipping.city || '');
      this.setShippingField('province', shipping.province || shipping.province_code || '');
      this.setShippingField('country', shipping.country || shipping.country_code || '');
      const editable = Boolean(ctx?.editable_shipping_address);
      this.querySelectorAll('[data-b2b-shipping-field]').forEach((input) => { input.disabled = !editable; });
      this.updateLocalTotals();
    }

    shippingPayload() {
      const values = {};
      this.querySelectorAll('[data-b2b-shipping-field]').forEach((input) => {
        values[input.dataset.b2bShippingField] = input.value.trim();
      });
      return values;
    }

    orderPayload() {
      return {
        cart_token: this.config?.cart_token || null,
        request_id: this.requestId || null,
        company_location_id: this.config?.company_location_id || this.dataset.companyLocationId,
        items: Array.isArray(this.config?.items)
          ? this.config.items.map((item) => ({
              variant_id: String(item.variant_id || ''),
              quantity: Number(item.quantity) || 0,
              campaign_color: this.itemProperty(item, 'Piel / Color'),
              campaign_floor: this.itemProperty(item, 'Piso'),
              campaign_model: this.itemProperty(item, '_Campaña modelo'),
              campaign_section: this.itemProperty(item, '_Campaña sección'),
              campaign_virtual: this.itemProperty(item, '_Campaña virtual') === '1',
              campaign_price_source_variant_id: this.itemProperty(item, '_Campaña precio fuente variante'),
            })).filter((item) => item.variant_id && item.quantity > 0)
          : [],
        payment_mode: this.currentPaymentMode(),
        po_number: this.querySelector('[data-b2b-po-number]')?.value.trim() || '',
        note: this.combinedOrderNote(),
        delivery_fortnight: this.isCampaignOrder ? this.globalFortnightValue() : '',
        shipping_address: this.shippingPayload(),
      };
    }

    applyPreview(preview) {
      if (!preview) return;
      this.preview = preview;

      // El servidor B2B devuelve la dirección validada por Shopify junto al
      // cálculo. La rehidratamos aquí para que ninguna actualización del
      // resumen económico pueda dejar vacíos los campos de envío.
      const previewShipping = preview?.shipping_address || {};
      if (Object.keys(previewShipping).length) {
        const editable = Boolean(this.context?.editable_shipping_address);
        const syncField = (name, value) => {
          const input = this.querySelector(`[data-b2b-shipping-field="${name}"]`);
          if (!input || value == null) return;
          // Si Shopify permite editar la dirección, respetamos cualquier cambio
          // que el comprador ya haya escrito y solo completamos campos vacíos.
          if (!editable || !input.value.trim()) input.value = String(value);
        };
        syncField('company', previewShipping.company || this.context?.company?.name || '');
        syncField('phone', previewShipping.phone || this.context?.phone || '');
        syncField('address1', previewShipping.address1 || '');
        syncField('address2', previewShipping.address2 || '');
        syncField('zip', previewShipping.zip || '');
        syncField('city', previewShipping.city || '');
        syncField('province', previewShipping.province || previewShipping.province_code || '');
        syncField('country', previewShipping.country || previewShipping.country_code || '');
      }

      // La dirección registrada de facturación también se pinta con los datos
      // reales devueltos por el servidor, en vez de depender solo del Liquid
      // inicial de Shopify.
      const previewBilling = preview?.billing_address || {};
      const billing = this.querySelector('[data-b2b-billing-address]');
      if (billing && Object.keys(previewBilling).length) {
        const lines = [
          previewBilling.address1,
          previewBilling.address2,
          [previewBilling.zip, previewBilling.city].filter(Boolean).join(' '),
          [previewBilling.province, previewBilling.country].filter(Boolean).join(', '),
        ].filter(Boolean);
        billing.replaceChildren();
        (lines.length ? lines : ['—']).forEach((line, index) => {
          if (index) billing.appendChild(document.createElement('br'));
          billing.appendChild(document.createTextNode(String(line)));
        });
      }

      const cents = (key) => Math.round(Number(preview[key] || 0) * 100);
      if (preview.pairs != null) {
        this.pairs = Number(preview.pairs) || this.pairs;
        const pairs = this.querySelector('[data-b2b-total-pairs]');
        if (pairs) pairs.textContent = String(preview.pairs);
      }
      const habitual = cents('habitual_discount');
      const original = cents('original_merchandise');
      const merchandise = cents('merchandise');
      const habitualRow = this.querySelector('[data-b2b-habitual-row]');
      const originalRow = this.querySelector('[data-b2b-original-row]');
      const habitualLabel = this.querySelector('[data-b2b-habitual-label]');
      this.habitualCents = habitual;
      this.originalCents = original;
      this.merchandiseCents = merchandise;
      this.habitualPercent = Number(preview?.habitual_discount_percent || 0) || 0;
      if (habitualRow) habitualRow.hidden = habitual <= 0 || this.habitualPercent <= 0;
      if (originalRow) originalRow.hidden = habitual <= 0 || this.habitualPercent <= 0;
      if (habitualLabel) {
        const rounded = Math.round(this.habitualPercent * 10) / 10;
        const percentText = Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',');
        habitualLabel.textContent = habitual > 0 ? `Descuento habitual (${percentText} %)` : 'Descuento habitual';
      }
      this.setMoney('[data-b2b-original-merchandise]', original);
      this.setMoney('[data-b2b-habitual-discount]', habitual, '−');
      this.setMoney('[data-b2b-merchandise]', merchandise);
      const early = cents('early_payment_discount');
      const earlyRow = this.querySelector('[data-b2b-early-row]');
      if (earlyRow) earlyRow.hidden = early <= 0;
      this.setMoney('[data-b2b-early-discount]', early, '−');
      const shipping = this.querySelector('[data-b2b-shipping-amount]');
      if (shipping) shipping.textContent = cents('shipping') === 0 ? 'GRATIS' : this.money(cents('shipping'));
      this.setMoney('[data-b2b-tax-base]', cents('tax_base'));
      this.setMoney('[data-b2b-iva]', cents('iva'));
      this.hasRE = Number(preview.re || 0) > 0 || Boolean(this.context?.recargo_equivalencia);
      const reRow = this.querySelector('[data-b2b-re-row]');
      if (reRow) reRow.hidden = !this.hasRE;
      this.setMoney('[data-b2b-re]', cents('re'));
      this.setMoney('[data-b2b-grand-total]', cents('total'));
      this.calculation = {
        earlyCents: early,
        shippingCents: cents('shipping'),
        baseCents: cents('tax_base'),
        ivaCents: cents('iva'),
        reCents: cents('re'),
        totalCents: cents('total'),
      };
      const note = this.querySelector('.clayan-b2b-final-order__calculation-note');
      if (note) note.textContent = 'Importe validado por el servicio B2B de Clayan con precios, portes e impuestos que se usarán al crear el pedido.';
    }

    async refreshPreview() {
      if (!this.serviceReady) return;
      const preview = await this.fetchJSON(this.proxyUrl('/preview'), {
        method: 'POST',
        body: JSON.stringify(this.orderPayload()),
      });
      if (preview.pairs === undefined || preview.total === undefined) {
        throw new Error('El servidor B2B no ha confirmado el cálculo del pedido.');
      }
      this.applyPreview(preview);
    }

    async loadContextAndPreview() {
      if (!this.config?.customer_id || !this.config?.company_location_id) {
        this.setServiceStatus('Falta contexto B2B', 'error');
        this.setMessage('No se ha podido identificar correctamente el cliente o la empresa B2B.', true);
        this.updateSubmitAvailability();
        return;
      }
      try {
        this.setServiceStatus('Conectando servicio B2B…', 'loading');
        const ctx = await this.fetchJSON(this.proxyUrl('/context'));
        if (!ctx?.customer?.id || !ctx?.company?.id || !ctx?.company_location?.id) {
          throw new Error('El servidor no ha validado la identidad y empresa B2B.');
        }
        this.serviceReady = true;
        this.applyContext(ctx);
        await this.refreshPreview();
        this.setServiceStatus('Pedido listo para enviar', 'ready');
        this.setMessage('');
      } catch (error) {
        this.serviceReady = false;
        this.setServiceStatus('Servicio B2B sin confirmar', 'error');
        this.setMessage('No se ha podido verificar la conexión o el cálculo del B2B. No se ha iniciado ningún envío de pedido. Vuelve a cargar esta página cuando se restablezca el servicio.', true);
        const note = this.querySelector('.clayan-b2b-final-order__calculation-note');
        if (note) note.textContent = 'Precios definitivos pendientes de validación del servidor B2B.';
      } finally {
        this.updateSubmitAvailability();
      }
    }

    clearPersistentBackupAfterOrder() {
      try {
        const customerId = document.body?.dataset?.clayanB2bCustomerId || this.config?.customer_id || '';
        if (customerId) localStorage.removeItem(`clayan_b2b_cart_v1:${customerId}`);
      } catch (error) {}
    }

    async submitOrder() {
      if (!this.submitButton || this.orderSending || this.orderOutcomeUncertain || this.orderConfirmed) return;
      if (this.mixedOrder) {
        this.setMessage('No se pueden mezclar campaña y reposición. Separa los artículos en dos pedidos.', true);
        return;
      }
      if (this.isCampaignOrder && !this.globalFortnightValue()) {
        this.setMessage('Selecciona la quincena general de entrega antes de enviar el pedido.', true);
        this.globalFortnight?.focus?.();
        this.updateSubmitAvailability();
        return;
      }
      if (this.submitButton.disabled) return;
      if (!this.confirm?.checked) {
        this.setMessage('Marca la casilla de revisión antes de enviar el pedido.', true);
        this.confirm?.focus();
        return;
      }
      if (!this.serviceReady) {
        this.setMessage('No se ha podido verificar la conexión con el B2B. No se ha enviado ningún pedido.', true);
        return;
      }

      const originalText = this.submitButton.textContent;
      this.orderSending = true;
      this.updateSubmitAvailability();
      this.submitButton.setAttribute('aria-busy', 'true');
      this.submitButton.textContent = 'ENVIANDO PEDIDO…';
      this.setMessage('Registrando el pedido. Por favor, no cierres esta página…');
      this.setServiceStatus('Creando pedido…', 'loading');

      // Distingue un fallo previo a POST /order de un POST cuyo resultado desconocemos.
      let orderRequestStarted = false;
      try {
        await this.refreshPreview();
        this.saveOrderSnapshot();
        const finalPayload = this.orderPayload();
        finalPayload.print_snapshot = this.orderSnapshot();
        orderRequestStarted = true;
        const result = await this.fetchJSON(this.proxyUrl('/order'), {
          method: 'POST',
          body: JSON.stringify(finalPayload),
        });
        if (result.ok !== true || !result.order_id || !result.order_name) {
          throw new Error('El servidor no ha confirmado el número e identificador del pedido.');
        }
        this.orderConfirmed = true;

        // Un fallo de cart/clear.js NO implica que haya fallado la creación del pedido.
        let cartCleared = false;
        try {
          const root = window.Shopify?.routes?.root || '/';
          const cleared = await fetch(`${root}cart/clear.js`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({}),
          });
          if (!cleared.ok) throw new Error('Shopify no ha vaciado el carrito.');
          cartCleared = true;
          this.clearPersistentBackupAfterOrder();
          try { sessionStorage.removeItem('clayanB2BOrderRequestId'); } catch (error) {}
        } catch (error) {
          cartCleared = false;
        }

        this.querySelector('.clayan-b2b-final-order__grid')?.setAttribute('hidden', '');
        this.querySelector('.clayan-b2b-final-order__submit-area')?.setAttribute('hidden', '');
        this.querySelector('.clayan-b2b-final-order__header')?.setAttribute('hidden', '');
        if (this.success) this.success.hidden = false;
        const copy = this.querySelector('[data-b2b-success-copy]');
        if (copy) {
          const orderName = result.order_name;
          let text;
          if (result.gcs_pending === true || result.gcs_synced === false) {
            text = `${orderName} se ha registrado en Shopify. Su integración en GCS está pendiente de revisión. No vuelvas a enviarlo.`;
          } else if (result.gcs_synced === true) {
            text = `${orderName} se ha registrado en Shopify y GCS.`;
          } else {
            text = `${orderName} se ha registrado en Shopify. La confirmación de GCS sigue pendiente.`;
          }
          if (result.emails_queued === true) {
            text += ' Los correos de confirmación están en proceso de envío.';
          } else if (result.customer_email_sent === true && result.factory_email_sent === true) {
            text += ' El servidor ha confirmado los envíos de correo.';
          } else {
            text += ' La recepción de los correos aún no está confirmada.';
          }
          if (!cartCleared) {
            text += ' Atención: Shopify no ha confirmado el vaciado del carrito. El pedido ya figura registrado; NO pulses Enviar otra vez.';
          }
          copy.textContent = text;
        }
        if (cartCleared) updateShippingUI(0);
      } catch (error) {
        if (orderRequestStarted) {
          // Una respuesta perdida no demuestra que Shopify/GCS no hayan creado el pedido.
          this.orderOutcomeUncertain = true;
          this.setServiceStatus('Pedido pendiente de verificar', 'error');
          this.setMessage('No hemos podido confirmar si el pedido quedó registrado. NO vuelvas a pulsar Enviar ni crees otro pedido: contacta con Clayan para comprobar Shopify y GCS. Tu carrito se conserva.', true);
        } else {
          this.serviceReady = false;
          this.setServiceStatus('Cálculo B2B no disponible', 'error');
          this.setMessage('No se ha podido validar el resumen. Todavía NO se ha enviado la solicitud del pedido. El carrito se conserva; vuelve a cargar la página para reintentar la conexión.', true);
        }
      } finally {
        this.orderSending = false;
        this.submitButton.removeAttribute('aria-busy');
        if (!this.orderConfirmed) this.submitButton.textContent = originalText;
        this.updateSubmitAvailability();
      }
    }

  }

  if (!customElements.get('clayan-b2b-final-order')) {
    customElements.define('clayan-b2b-final-order', ClayanB2BFinalOrder);
  }

  const escapeHTML = (value) => String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#039;');

  const renderVisualOrderForPrint = () => {
    const sheet = document.querySelector('[data-b2b-print-sheet]');
    if (!sheet) return false;
    // Mover el documento de impresión al BODY evita que los contenedores
    // del tema/cart conserven altura y creen páginas en blanco al imprimir.
    if (sheet.parentElement !== document.body) document.body.appendChild(sheet);
    let snapshot = null;
    try { snapshot = JSON.parse(sessionStorage.getItem('clayan_b2b_last_order_visual_v1') || 'null'); } catch (error) {}
    if (!snapshot) {
      sheet.innerHTML = '<p>No se ha podido recuperar el detalle del pedido para imprimir.</p>';
      return false;
    }
    const sizes = Array.from({ length: 15 }, (_, i) => 35 + i);
    const rows = Array.isArray(snapshot.rows) ? snapshot.rows : [];
    const rowHTML = rows.map((row) => {
      const cells = sizes.map((size) => `<td>${Number(row?.sizes?.[size] || 0) || ''}</td>`).join('');
      return `<tr><td>${escapeHTML(row.model || '—')}</td><td>${escapeHTML(row.description || '—')}</td>${cells}<td>${escapeHTML(row.total || 0)}</td></tr>`;
    }).join('');
    const totals = snapshot.totals || {};
    sheet.innerHTML = `
      <div class="clayan-b2b-print-doc">
        <header class="clayan-b2b-print-doc__top">
          <div><div class="clayan-b2b-print-doc__brand">CLAYAN SHOES, S.L.</div><div class="clayan-b2b-print-doc__title">PEDIDO B2B</div></div>
          <div class="clayan-b2b-print-doc__meta">
            <div><span class="clayan-b2b-print-doc__label">Fecha</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.dateText || '')}</span></div>
            <div><span class="clayan-b2b-print-doc__label">Total pares</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.totalPairs || 0)}</span></div>
            <div><span class="clayan-b2b-print-doc__label">Referencia</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.reference || '—')}</span></div>
            <div><span class="clayan-b2b-print-doc__label">Forma de pago</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.payment || '—')}</span></div>
            ${snapshot.deliveryFortnight ? `<div><span class="clayan-b2b-print-doc__label">Quincena campaña</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.deliveryFortnight)}</span></div>` : ''}
          </div>
        </header>
        <section class="clayan-b2b-print-doc__info">
          <div><span class="clayan-b2b-print-doc__label">Empresa</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.company || '—')}</span></div>
          <div><span class="clayan-b2b-print-doc__label">Contacto</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.contact || '—')}<br>${escapeHTML(snapshot.email || '—')}</span></div>
          <div><span class="clayan-b2b-print-doc__label">NIF / CIF</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.taxId || '—')}</span></div>
          <div><span class="clayan-b2b-print-doc__label">Agente comercial</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.agent || '—')}</span></div>
          <div><span class="clayan-b2b-print-doc__label">Teléfono</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.phone || '—')}</span></div>
        </section>
        <div><span class="clayan-b2b-print-doc__label">Dirección de envío</span><span class="clayan-b2b-print-doc__value">${escapeHTML(snapshot.shipping || '—').replace(/\n/g, ' · ')}</span></div>
        <table class="clayan-b2b-print-doc__table">
          <thead><tr><th>Modelo</th><th>Descripción</th>${sizes.map((s) => `<th>${s}</th>`).join('')}<th>Total</th></tr></thead>
          <tbody>${rowHTML || '<tr><td colspan="18">Sin líneas de pedido</td></tr>'}</tbody>
        </table>
        <section class="clayan-b2b-print-doc__bottom">
          <div class="clayan-b2b-print-doc__notes">
            <span class="clayan-b2b-print-doc__label">Observaciones</span>
            <div>${escapeHTML(snapshot.note || '—')}</div>
          </div>
          <table class="clayan-b2b-print-doc__totals">
            <tbody>
              ${totals.hasHabitual ? `<tr><td>Subtotal antes de descuentos</td><td>${escapeHTML(totals.original || '—')}</td></tr><tr><td>${escapeHTML(totals.habitualLabel || 'Descuento habitual')}</td><td>${escapeHTML(totals.habitual || '—')}</td></tr>` : ''}
              <tr><td>Mercancía B2B sin IVA</td><td>${escapeHTML(totals.merchandise || '—')}</td></tr>
              <tr><td>Pronto pago −3 %</td><td>${escapeHTML(totals.early || '—')}</td></tr>
              <tr><td>Portes</td><td>${escapeHTML(totals.shipping || '—')}</td></tr>
              <tr><td>Base imponible</td><td>${escapeHTML(totals.base || '—')}</td></tr>
              <tr><td>IVA 21 %</td><td>${escapeHTML(totals.iva || '—')}</td></tr>
              <tr><td>R.E. 5,2 %</td><td>${escapeHTML(totals.re || '—')}</td></tr>
              <tr><td>TOTAL PEDIDO</td><td>${escapeHTML(totals.total || '—')}</td></tr>
            </tbody>
          </table>
        </section>
        <footer class="clayan-b2b-print-doc__footer">Pedido recibido desde el portal profesional de Clayan Shoes.</footer>
      </div>`;
    return true;
  };

  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('[data-b2b-print-sheet]')) renderVisualOrderForPrint();
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-b2b-print-order]');
    if (!button) return;
    event.preventDefault();
    const order = document.querySelector('clayan-b2b-final-order');
    if (order && typeof order.saveOrderSnapshot === 'function') order.saveOrderSnapshot();
    if (renderVisualOrderForPrint()) window.print();
  });
  const persistentCartCustomerId = document.body?.dataset?.clayanB2bCustomerId || '';
  const persistentCartKey = persistentCartCustomerId ? `clayan_b2b_cart_v1:${persistentCartCustomerId}` : '';
  let persistentCartRestoring = false;

  const normalizePersistentItems = (cartOrItems) => {
    const items = Array.isArray(cartOrItems) ? cartOrItems : (Array.isArray(cartOrItems?.items) ? cartOrItems.items : []);
    return items
      .map((item) => ({
        id: Number(item.id || item.variant_id || 0),
        quantity: Math.max(0, Number(item.quantity || 0)),
      }))
      .filter((item) => Number.isFinite(item.id) && item.id > 0 && item.quantity > 0);
  };

  const readPersistentCart = () => {
    if (!persistentCartKey) return null;
    try {
      const parsed = JSON.parse(localStorage.getItem(persistentCartKey) || 'null');
      if (!parsed || !Array.isArray(parsed.items)) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  };

  const writePersistentCart = (cartOrItems) => {
    if (!persistentCartKey || persistentCartRestoring) return;
    const items = normalizePersistentItems(cartOrItems);
    try {
      if (!items.length) {
        localStorage.removeItem(persistentCartKey);
        return;
      }
      localStorage.setItem(persistentCartKey, JSON.stringify({
        customer_id: persistentCartCustomerId,
        saved_at: Date.now(),
        items,
      }));
    } catch (error) {
      // Si el navegador bloquea localStorage, el carrito nativo sigue funcionando.
    }
  };

  const fetchCurrentCart = async () => {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('No se pudo leer el carrito.');
    return response.json();
  };

  const restorePersistentCart = async () => {
    if (!persistentCartKey || persistentCartRestoring) return;
    const saved = readPersistentCart();
    if (!saved?.items?.length) {
      try {
        const current = await fetchCurrentCart();
        if ((current?.item_count || 0) > 0) writePersistentCart(current);
      } catch (error) {}
      return;
    }

    try {
      const current = await fetchCurrentCart();
      if ((current?.item_count || 0) > 0) {
        writePersistentCart(current);
        return;
      }

      persistentCartRestoring = true;
      const root = window.Shopify?.routes?.root || '/';
      const savedItems = normalizePersistentItems(saved.items);
      if (!savedItems.length) return;

      let restoredAny = false;
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          credentials: 'same-origin',
          body: JSON.stringify({ items: savedItems }),
        });
        restoredAny = response.ok;
      } catch (error) {}

      // Si una variante ya no se puede vender, intentamos restaurar el resto una a una.
      if (!restoredAny) {
        for (const item of savedItems) {
          try {
            const response = await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
              },
              credentials: 'same-origin',
              body: JSON.stringify({ items: [item] }),
            });
            if (response.ok) restoredAny = true;
          } catch (error) {}
        }
      }

      if (restoredAny) {
        const restoredCart = await fetchCurrentCart();
        try {
          localStorage.setItem(persistentCartKey, JSON.stringify({
            customer_id: persistentCartCustomerId,
            saved_at: Date.now(),
            items: normalizePersistentItems(restoredCart),
          }));
        } catch (error) {}
        updateShippingUI(restoredCart.item_count || 0);

        // Recargar garantiza que el drawer, el resumen y todas las secciones
        // de Dawn reflejen inmediatamente el carrito restaurado.
        window.location.reload();
      }
    } catch (error) {
      // Restauración no crítica: nunca debe bloquear la compra.
    } finally {
      persistentCartRestoring = false;
    }
  };

  const syncPersistentCart = async () => {
    if (!persistentCartKey || persistentCartRestoring) return;
    try {
      const cart = await fetchCurrentCart();
      writePersistentCart(cart);
    } catch (error) {}
  };



  // V12.6 — vaciar carrito B2B completo (carrito actual + persistencia local).
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-b2b-clear-cart]');
    if (!button) return;
    event.preventDefault();

    if (!window.confirm('¿Quieres vaciar todo el carrito?')) return;

    const originalText = button.textContent;
    button.disabled = true;
    button.textContent = 'Vaciando…';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/clear.js`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error('No se pudo vaciar el carrito.');

      if (persistentCartKey) {
        try { localStorage.removeItem(persistentCartKey); } catch (error) {}
      }
      try { sessionStorage.removeItem('clayanB2BOrderRequestId'); } catch (error) {}

      window.location.reload();
    } catch (error) {
      button.disabled = false;
      button.textContent = originalText;
      window.alert('No se ha podido vaciar el carrito. Inténtalo de nuevo.');
    }
  });

  if (persistentCartKey) {
    document.addEventListener('DOMContentLoaded', () => {
      restorePersistentCart();
    });

    // Guardamos después de cambios de carrito emitidos por Dawn/tema.
    document.addEventListener('cart:updated', () => {
      window.setTimeout(syncPersistentCart, 80);
    });

    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        window.setTimeout(syncPersistentCart, 80);
      });
    } else if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
      subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
        window.setTimeout(syncPersistentCart, 80);
      });
    }

    // Cubre actualizaciones nativas de cantidad/eliminación aunque el tema no
    // emita el evento anterior en alguna vista concreta.
    document.addEventListener('change', (event) => {
      if (event.target?.matches?.('input[name="updates[]"], [data-quantity-variant-id]')) {
        window.setTimeout(syncPersistentCart, 650);
      }
    });
  }


  /* ========================================================================
     V13.1 — CARRITO B2B AGRUPADO + RESUMEN VISUAL STICKY
     Presentación solamente: agrupa tallas por modelo/acabado y mantiene
     intacto el carrito Shopify y la revisión final/servidor existentes.
     ======================================================================== */
  const b2bCartProp = (item, names) => {
    const props = item?.properties && typeof item.properties === 'object' ? item.properties : {};
    for (const name of names) {
      const value = props[name];
      if (value !== undefined && value !== null && String(value).trim() !== '') return String(value).trim();
    }
    return '';
  };

  const b2bCartModel = (item) => {
    const title = String(item?.product_title || item?.title || '').trim();
    const match = title.match(/^\s*(\d{2,5})\b/);
    if (match) return match[1];
    return title.split('·')[0].trim() || title;
  };

  const b2bCartSize = (item) => {
    const values = Array.isArray(item?.variant_options) ? item.variant_options.filter(Boolean) : [];
    if (values.length) return String(values[0]);
    const title = String(item?.variant_title || '').trim();
    return title && title !== 'Default Title' ? title.split('/')[0].trim() : '—';
  };

  const b2bCartImage = (item) => {
    return item?.featured_image?.url || item?.image || '';
  };

  const groupB2BCartItems = (cart) => {
    const groups = new Map();
    (cart?.items || []).forEach((item) => {
      const campaign = b2bCartProp(item, ['Campaña', 'Campana']);
      const color = b2bCartProp(item, ['Piel / Color', 'Piel/Color', 'Color campaña', 'Color Campaña']);
      const sole = b2bCartProp(item, ['Piso', 'Piso campaña', 'Piso Campaña']);
      const model = b2bCartModel(item);
      const fallbackTitle = String(item?.product_title || item?.title || '').trim();
      const key = [model || item.product_id, campaign, color, sole, item.product_id].join('||');
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          model,
          title: fallbackTitle,
          campaign,
          color,
          sole,
          image: b2bCartImage(item),
          lines: [],
          pairs: 0,
          amount: 0,
        });
      }
      const group = groups.get(key);
      const campaignUnit = Number(b2bCartProp(item, ['_Campaña precio céntimos']) || 0);
      const unit = campaignUnit > 0 ? campaignUnit : Number(item.final_price ?? item.price ?? 0);
      const amount = unit * Number(item.quantity || 0);
      group.lines.push({
        key: item.key,
        size: b2bCartSize(item),
        quantity: Number(item.quantity || 0),
        amount,
        unit,
      });
      group.pairs += Number(item.quantity || 0);
      group.amount += amount;
    });
    return Array.from(groups.values());
  };

  const b2bGroupTitle = (group) => {
    const bits = [];
    if (group.model) bits.push(group.model);
    if (group.color) bits.push(group.color);
    if (group.sole) bits.push(group.sole);
    return bits.length ? bits.join(' · ') : group.title;
  };

  const b2bRenderSizeLine = (line, currency) => {
    const fmt = moneyFormatter(currency);
    return `
      <div class="clayan-b2b-grouped-size" data-b2b-grouped-line="${escapeHTML(line.key)}">
        <div class="clayan-b2b-grouped-size__info">
          <strong>Talla ${escapeHTML(line.size)}</strong>
          <span>${fmt.format((line.unit || 0) / 100)} / par</span>
        </div>
        <div class="clayan-b2b-grouped-size__qty" aria-label="Cantidad talla ${escapeHTML(line.size)}">
          <button type="button" data-b2b-grouped-qty="minus" aria-label="Quitar un par">−</button>
          <strong>${line.quantity}</strong>
          <button type="button" data-b2b-grouped-qty="plus" aria-label="Añadir un par">+</button>
          <button type="button" class="clayan-b2b-grouped-size__remove" data-b2b-grouped-qty="remove" aria-label="Eliminar talla">×</button>
        </div>
        <strong class="clayan-b2b-grouped-size__total">${fmt.format((line.amount || 0) / 100)}</strong>
      </div>`;
  };

  const b2bRenderGroup = (group, currency) => {
    const fmt = moneyFormatter(currency);
    const image = group.image
      ? `<img src="${escapeHTML(group.image)}" alt="${escapeHTML(b2bGroupTitle(group))}" loading="lazy">`
      : '<span class="clayan-b2b-grouped-card__placeholder">Sin foto</span>';
    const meta = [group.campaign ? `Campaña: ${group.campaign}` : '', group.color ? `Piel / Color: ${group.color}` : '', group.sole ? `Piso: ${group.sole}` : '']
      .filter(Boolean)
      .map((value) => `<span>${escapeHTML(value)}</span>`)
      .join('');
    return `
      <article class="clayan-b2b-grouped-card">
        <div class="clayan-b2b-grouped-card__head">
          <div class="clayan-b2b-grouped-card__media">${image}</div>
          <div class="clayan-b2b-grouped-card__identity">
            <h3>${escapeHTML(b2bGroupTitle(group))}</h3>
            <div class="clayan-b2b-grouped-card__meta">${meta}</div>
          </div>
          <div class="clayan-b2b-grouped-card__group-total">
            <span>${group.pairs} ${group.pairs === 1 ? 'par' : 'pares'}</span>
            <strong>${fmt.format((group.amount || 0) / 100)}</strong>
            <button type="button" class="clayan-b2b-grouped-card__toggle" data-b2b-group-lines-toggle aria-expanded="false">Ver tallas</button>
          </div>
        </div>
        <div class="clayan-b2b-grouped-card__sizes" data-b2b-group-lines hidden>
          ${group.lines.map((line) => b2bRenderSizeLine(line, currency)).join('')}
        </div>
      </article>`;
  };

  const b2bSummaryGroup = (group, currency) => {
    const fmt = moneyFormatter(currency);
    const sizes = group.lines.map((line) => `${escapeHTML(line.size)} × ${line.quantity}`).join(' · ');
    const image = group.image ? `<img src="${escapeHTML(group.image)}" alt="" loading="lazy">` : '';
    return `
      <div class="clayan-b2b-live-summary__item">
        <div class="clayan-b2b-live-summary__thumb">${image}</div>
        <div class="clayan-b2b-live-summary__copy">
          <strong>${escapeHTML(b2bGroupTitle(group))}</strong>
          <span>${sizes}</span>
          <small>${group.pairs} ${group.pairs === 1 ? 'par' : 'pares'}</small>
        </div>
        <strong class="clayan-b2b-live-summary__price">${fmt.format((group.amount || 0) / 100)}</strong>
      </div>`;
  };

  const b2bMirrorFinalTotals = (summary, cart) => {
    if (!summary) return;
    const fmt = moneyFormatter(cart?.currency || 'EUR');
    const map = [
      ['pairs', '[data-b2b-total-pairs]', String(cart?.item_count || 0)],
      ['merchandise', '[data-b2b-merchandise]', fmt.format(groups.reduce((sum, group) => sum + Number(group.amount || 0), 0) / 100)],
      ['shipping', '[data-b2b-shipping-amount]', (cart?.item_count || 0) >= FREE_SHIPPING_THRESHOLD ? 'GRATIS' : '10,00 €'],
      ['taxbase', '[data-b2b-tax-base]', '—'],
      ['iva', '[data-b2b-iva]', '—'],
      ['re', '[data-b2b-re]', '—'],
      ['grand', '[data-b2b-grand-total]', '—'],
    ];
    map.forEach(([name, sourceSelector, fallback]) => {
      const target = summary.querySelector(`[data-b2b-live-total="${name}"]`);
      if (!target) return;
      const source = document.querySelector(sourceSelector);
      target.textContent = (source?.textContent || '').trim() || fallback;
    });
    const reRow = summary.querySelector('[data-b2b-live-re-row]');
    const sourceReRow = document.querySelector('[data-b2b-re-row]');
    if (reRow) reRow.hidden = !sourceReRow || sourceReRow.hidden;

    const fortnight = document.querySelector('[data-b2b-global-fortnight]');
    const fortnightTarget = summary.querySelector('[data-b2b-live-fortnight]');
    if (fortnightTarget) fortnightTarget.textContent = fortnight?.value || 'Pendiente de seleccionar';
  };

  const renderB2BGroupedCart = async () => {
    if (!document.body?.classList.contains('clayan-b2b-customer')) return;
    const main = document.querySelector('#main-cart-items');
    if (!main || main.dataset.b2bGroupedMounted === 'true') return;

    let cart;
    try { cart = await fetchCurrentCart(); } catch (error) { return; }
    if (!cart?.items?.length) return;

    const groups = groupB2BCartItems(cart);
    if (!groups.length) return;
    const currency = cart.currency || 'EUR';

    const workspace = document.createElement('div');
    workspace.className = 'clayan-b2b-cart-workspace';
    workspace.setAttribute('data-b2b-cart-workspace', '');
    workspace.innerHTML = `
      <section class="clayan-b2b-grouped-cart" aria-label="Productos del pedido">
        <div class="clayan-b2b-grouped-cart__header">
          <div>
            <span class="clayan-b2b-eyebrow">Pedido profesional</span>
            <h2>Modelos, tallas y cantidades</h2>
          </div>
          <span>${cart.item_count} ${cart.item_count === 1 ? 'par' : 'pares'}</span>
        </div>
        <div class="clayan-b2b-grouped-cart__list">${groups.map((group) => b2bRenderGroup(group, currency)).join('')}</div>
      </section>
      <aside class="clayan-b2b-live-summary" data-b2b-live-summary aria-label="Resumen del pedido">
        <div class="clayan-b2b-live-summary__head">
          <div>
            <span class="clayan-b2b-eyebrow">Vista previa</span>
            <h2>Resumen del pedido</h2>
          </div>
          <button type="button" class="clayan-b2b-live-summary__close" data-b2b-summary-close aria-label="Cerrar resumen">×</button>
        </div>
        <div class="clayan-b2b-live-summary__items">${groups.map((group) => b2bSummaryGroup(group, currency)).join('')}</div>
        <div class="clayan-b2b-live-summary__totals">
          <div><span>Total pares</span><strong data-b2b-live-total="pairs">${cart.item_count}</strong></div>
          <div><span>Mercancía B2B sin IVA</span><strong data-b2b-live-total="merchandise">${moneyFormatter(currency).format(groups.reduce((sum, group) => sum + Number(group.amount || 0), 0) / 100)}</strong></div>
          <div><span>Portes</span><strong data-b2b-live-total="shipping">${cart.item_count >= FREE_SHIPPING_THRESHOLD ? 'GRATIS' : '10,00 €'}</strong></div>
          <div><span>Base imponible</span><strong data-b2b-live-total="taxbase">—</strong></div>
          <div><span>IVA 21 %</span><strong data-b2b-live-total="iva">—</strong></div>
          <div data-b2b-live-re-row hidden><span>R.E. 5,2 %</span><strong data-b2b-live-total="re">—</strong></div>
          <div class="clayan-b2b-live-summary__grand"><span>Total pedido</span><strong data-b2b-live-total="grand">—</strong></div>
        </div>
        <div class="clayan-b2b-live-summary__delivery">
          <span>Quincena de campaña</span>
          <strong data-b2b-live-fortnight>Pendiente de seleccionar</strong>
        </div>
        <button type="button" class="button clayan-b2b-live-summary__review" data-b2b-go-final>Revisar datos y enviar pedido</button>
      </aside>`;

    main.before(workspace);
    main.dataset.b2bGroupedMounted = 'true';
    document.body.classList.add('clayan-b2b-cart-grouped-ready');

    const summary = workspace.querySelector('[data-b2b-live-summary]');
    b2bMirrorFinalTotals(summary, cart);

    const finalOrder = document.querySelector('clayan-b2b-final-order');
    if (finalOrder && typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(() => b2bMirrorFinalTotals(summary, cart));
      observer.observe(finalOrder, { childList: true, subtree: true, characterData: true, attributes: true });
    }

    const fortnight = document.querySelector('[data-b2b-global-fortnight]');
    if (fortnight) fortnight.addEventListener('change', () => b2bMirrorFinalTotals(summary, cart));
  };

  const changeB2BGroupedLine = async (lineKey, quantity, button) => {
    const root = window.Shopify?.routes?.root || '/';
    if (button) button.disabled = true;
    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: lineKey, quantity: Math.max(0, quantity) }),
      });
      if (!response.ok) throw new Error('No se pudo actualizar la cantidad.');
      const cart = await response.json();
      if (persistentCartKey) writePersistentCart(cart);
      window.location.reload();
    } catch (error) {
      if (button) button.disabled = false;
      window.alert('No se ha podido actualizar esta talla. Inténtalo de nuevo.');
    }
  };

  document.addEventListener('click', (event) => {
    const groupToggle = event.target.closest('[data-b2b-group-lines-toggle]');
    if (groupToggle) {
      event.preventDefault();
      const card = groupToggle.closest('.clayan-b2b-grouped-card');
      const lines = card?.querySelector('[data-b2b-group-lines]');
      if (!lines) return;
      const willOpen = lines.hidden;
      lines.hidden = !willOpen;
      groupToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      groupToggle.textContent = willOpen ? 'Ocultar tallas' : 'Ver tallas';
      return;
    }

    const qtyButton = event.target.closest('[data-b2b-grouped-qty]');
    if (qtyButton) {
      event.preventDefault();
      const line = qtyButton.closest('[data-b2b-grouped-line]');
      if (!line) return;
      const key = line.getAttribute('data-b2b-grouped-line');
      const current = Number(line.querySelector('.clayan-b2b-grouped-size__qty strong')?.textContent || 0);
      const action = qtyButton.getAttribute('data-b2b-grouped-qty');
      const next = action === 'plus' ? current + 1 : action === 'minus' ? current - 1 : 0;
      changeB2BGroupedLine(key, next, qtyButton);
      return;
    }

    const open = event.target.closest('[data-b2b-summary-open]');
    if (open) {
      event.preventDefault();
      document.querySelector('[data-b2b-live-summary]')?.classList.add('is-open');
      document.documentElement.classList.add('clayan-b2b-summary-open');
      return;
    }
    const close = event.target.closest('[data-b2b-summary-close]');
    if (close) {
      event.preventDefault();
      document.querySelector('[data-b2b-live-summary]')?.classList.remove('is-open');
      document.documentElement.classList.remove('clayan-b2b-summary-open');
      return;
    }
    const goFinal = event.target.closest('[data-b2b-go-final]');
    if (goFinal) {
      event.preventDefault();
      document.querySelector('clayan-b2b-final-order')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      document.querySelector('[data-b2b-live-summary]')?.classList.remove('is-open');
      document.documentElement.classList.remove('clayan-b2b-summary-open');
    }
  });

  // Galería superior del modelo en Campaña Verano 27.
  (() => {
    let items = [];
    let index = 0;

    const lightbox = () => document.querySelector('[data-b2b-model-lightbox]');
    const image = () => document.querySelector('[data-b2b-model-lightbox-image]');
    const counter = () => document.querySelector('[data-b2b-model-lightbox-counter]');

    const render = () => {
      const box = lightbox();
      const img = image();
      if (!box || !img || !items.length) return;
      const item = items[index];
      img.src = item.src;
      img.alt = item.alt || 'Foto ampliada del modelo';
      const c = counter();
      if (c) c.textContent = items.length > 1 ? `${index + 1} / ${items.length}` : '';
      box.querySelector('[data-b2b-model-lightbox-prev]')?.toggleAttribute('hidden', items.length < 2);
      box.querySelector('[data-b2b-model-lightbox-next]')?.toggleAttribute('hidden', items.length < 2);
    };

    const open = (startNode) => {
      const nodes = Array.from(document.querySelectorAll('[data-b2b-model-gallery-item]'));
      items = nodes.map((node) => {
        const img = node.querySelector('img');
        return {
          src: String(img?.currentSrc || img?.src || node.dataset.imageUrl || '').trim(),
          alt: String(img?.alt || node.dataset.b2bMediaAlt || '').trim(),
          node,
        };
      }).filter((item) => item.src);
      if (!items.length) return;
      index = Math.max(0, items.findIndex((item) => item.node === startNode));
      render();
      const box = lightbox();
      if (box) {
        box.hidden = false;
        document.documentElement.classList.add('clayan-b2b-model-lightbox-open');
      }
    };

    const close = () => {
      const box = lightbox();
      if (box) box.hidden = true;
      items = [];
      index = 0;
      document.documentElement.classList.remove('clayan-b2b-model-lightbox-open');
    };

    const step = (dir) => {
      if (items.length < 2) return;
      index = (index + dir + items.length) % items.length;
      render();
    };

    document.addEventListener('click', (event) => {
      const item = event.target.closest('[data-b2b-model-gallery-item]');
      if (item) {
        event.preventDefault();
        open(item);
        return;
      }
      if (event.target.closest('[data-b2b-model-lightbox-close]')) {
        event.preventDefault();
        close();
        return;
      }
      if (event.target.closest('[data-b2b-model-lightbox-prev]')) {
        event.preventDefault();
        step(-1);
        return;
      }
      if (event.target.closest('[data-b2b-model-lightbox-next]')) {
        event.preventDefault();
        step(1);
      }
    });

    document.addEventListener('keydown', (event) => {
      const box = lightbox();
      if (!box || box.hidden) {
        const focusedItem = document.activeElement?.matches?.('[data-b2b-model-gallery-item]')
          ? document.activeElement
          : null;
        if (focusedItem && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          open(focusedItem);
        }
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        step(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        step(1);
      }
    });
  })();


  const initCampaignCollectionNavigation = async () => {
    const nav = document.querySelector('[data-b2b-campaign-category-nav]');
    const grid = document.querySelector('#product-grid');
    if (!nav || !grid) return;
    const empty = document.querySelector('[data-b2b-campaign-filter-empty]');

    const productKey = (card) => {
      const link = card.querySelector('a[href*="/products/"]');
      return link ? new URL(link.href, window.location.origin).pathname : card.textContent.trim();
    };

    // Shopify pagina la colección antes de que el filtro visual actúe. Cargamos
    // las demás páginas de campaña una sola vez para que la categoría se filtre
    // sobre TODO el catálogo, no solo sobre la página actual.
    try {
      const pageNumbers = Array.from(document.querySelectorAll('.pagination-wrapper a[href*="page="]'))
        .map((link) => Number(new URL(link.href, window.location.origin).searchParams.get('page') || 1))
        .filter((value) => Number.isFinite(value) && value > 0);
      const currentPage = Math.max(1, Number(new URL(window.location.href).searchParams.get('page') || 1) || 1);
      const maxPage = Math.min(12, Math.max(currentPage, ...pageNumbers, 1));
      const existing = new Set();
      const pageBuckets = new Map();
      pageBuckets.set(currentPage, Array.from(grid.querySelectorAll('[data-b2b-campaign-category]')).map((card) => card.cloneNode(true)));

      for (let page = 1; page <= maxPage; page += 1) {
        if (page === currentPage) continue;
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(page));
        url.searchParams.delete('b2b_category');
        const response = await fetch(url.toString(), {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) continue;
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        pageBuckets.set(page, Array.from(doc.querySelectorAll('#product-grid > [data-b2b-campaign-category]')));
      }

      if (maxPage > 1) {
        grid.innerHTML = '';
        for (let page = 1; page <= maxPage; page += 1) {
          (pageBuckets.get(page) || []).forEach((card) => {
            const key = productKey(card);
            if (!key || existing.has(key)) return;
            existing.add(key);
            grid.appendChild(document.importNode(card, true));
          });
        }
      }
      document.querySelector('.pagination-wrapper')?.setAttribute('hidden', '');
    } catch (error) {
      // Si falla una página remota, conservamos el catálogo ya cargado.
    }

    const cards = () => Array.from(grid.querySelectorAll('[data-b2b-campaign-category]'));
    const apply = (filter) => {
      let visible = 0;
      cards().forEach((card) => {
        const category = String(card.dataset.b2bCampaignCategory || 'otros');
        const show = filter === 'all' || category === filter;
        card.hidden = !show;
        if (show) visible += 1;
      });
      nav.querySelectorAll('[data-b2b-campaign-filter]').forEach((button) => {
        button.classList.toggle('is-active', button.dataset.b2bCampaignFilter === filter);
        button.setAttribute('aria-pressed', button.dataset.b2bCampaignFilter === filter ? 'true' : 'false');
      });
      if (empty) empty.hidden = visible > 0;
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        if (filter === 'all') url.searchParams.delete('b2b_category');
        else url.searchParams.set('b2b_category', filter);
        history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      } catch (error) {}
    };

    nav.addEventListener('click', (event) => {
      const button = event.target.closest('[data-b2b-campaign-filter]');
      if (!button) return;
      event.preventDefault();
      apply(button.dataset.b2bCampaignFilter || 'all');
      grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    let initial = new URL(window.location.href).searchParams.get('b2b_category') || 'all';
    if (!nav.querySelector(`[data-b2b-campaign-filter="${initial}"]`)) initial = 'all';
    apply(initial);
  };

  document.addEventListener('DOMContentLoaded', () => {
    initCampaignCollectionNavigation();
    renderB2BGroupedCart();
    if (document.body?.classList.contains('clayan-b2b-customer') && document.querySelector('#main-cart-items')) {
      const mobileButton = document.createElement('button');
      mobileButton.type = 'button';
      mobileButton.className = 'clayan-b2b-mobile-summary-button';
      mobileButton.setAttribute('data-b2b-summary-open', '');
      mobileButton.innerHTML = '<span>Resumen pedido</span><strong data-clayan-b2b-cart-count>0</strong>';
      document.body.appendChild(mobileButton);
      fetchCurrentCart().then((cart) => {
        mobileButton.querySelector('strong').textContent = String(cart?.item_count || 0);
      }).catch(() => {});
    }
  });

})();
